import { useState, useEffect, useMemo, useRef, Fragment } from "react";
import { api } from "@/lib/rn3";
import { toast } from "sonner";
import { Upload, FileText, Trash2, CheckCircle2, AlertTriangle, Wand2, Save, Sparkles, Inbox, Check, Send, Undo2, Download, Eye, EyeOff, Mail, Settings, Plus, X, Power } from "lucide-react";

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Lightweight typeahead combobox for picking a chart-of-accounts category on a
// txn row. Filters by code OR name as the user types. Keyboard: ↑/↓ to move,
// Enter to pick, Esc to close. Blur commits nothing (only Enter/click do), so
// half-typed strings don't overwrite the saved value.
const AccountCombobox = ({ value, accounts, onPick, onCreateNew, placeholder, testid, disabled }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [cursor, setCursor] = useState(0);
  const [rect, setRect] = useState(null);   // input viewport rect for fixed-position popup
  const inputRef = useRef(null);
  const blurTimer = useRef(null);

  const selected = accounts.find((a) => a.code === value) || null;
  const displayLabel = selected ? `${selected.code} · ${selected.name}` : "";

  const results = useMemo(() => {
    // Skip filtering work entirely when the dropdown is closed — each txn row
    // has one Combobox, and running this on every parent render across 59+
    // rows on a memory-constrained tab can push Chrome over its budget.
    if (!open) return [];
    const q = query.trim().toLowerCase();
    if (!q) return accounts;
    return accounts.filter((a) => {
      const hay = `${a.code} ${a.name} ${(a.type || "")} ${(a.detail_type || a.subtype || "")}`.toLowerCase();
      return hay.includes(q);
    });
  }, [open, query, accounts]);

  useEffect(() => { if (cursor >= results.length) setCursor(0); }, [results.length, cursor]);

  // Keep the popup glued to the input while open — recompute on scroll / resize
  // so it never drifts behind other panels or clips inside overflow containers.
  const measure = () => {
    if (inputRef.current) setRect(inputRef.current.getBoundingClientRect());
  };
  useEffect(() => {
    if (!open) return;
    measure();
    const onScroll = () => measure();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open]);

  const commit = (a) => {
    onPick(a ? a.code : "");
    setQuery("");
    setOpen(false);
    inputRef.current?.blur();
  };

  const onKeyDown = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter")) { setOpen(true); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setCursor((c) => Math.min(c + 1, Math.max(0, results.length - 1))); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setCursor((c) => Math.max(0, c - 1)); }
    else if (e.key === "Enter") { e.preventDefault(); if (results[cursor]) commit(results[cursor]); }
    else if (e.key === "Escape") { e.preventDefault(); setOpen(false); setQuery(""); inputRef.current?.blur(); }
  };

  // Compute where the popup should sit — below the input if there's room, above otherwise.
  const popupStyle = (() => {
    if (!rect) return { visibility: "hidden" };
    const gap = 2;
    const maxH = 340;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < 220 && rect.top > spaceBelow;
    const top = openUp ? Math.max(8, rect.top - maxH - gap) : rect.bottom + gap;
    const height = openUp ? Math.min(maxH, rect.top - gap - 8) : Math.min(maxH, window.innerHeight - rect.bottom - gap - 8);
    return {
      position: "fixed",
      top,
      left: rect.left,
      minWidth: Math.max(rect.width, 240),
      maxWidth: 360,
      maxHeight: height,
    };
  })();

  return (
    <div className="relative" data-testid={testid}>
      <input
        ref={inputRef}
        type="text"
        disabled={disabled}
        value={open ? query : displayLabel}
        placeholder={placeholder || "— ASK ACCOUNTANT —"}
        onFocus={() => { setOpen(true); setQuery(""); setCursor(0); measure(); }}
        onBlur={() => { blurTimer.current = setTimeout(() => setOpen(false), 120); }}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); setCursor(0); }}
        onKeyDown={onKeyDown}
        className="border border-[#E5E5E5] text-xs py-0.5 px-1 min-w-[180px] bg-white w-full uppercase tracking-wide"
        data-testid={testid ? `${testid}-input` : undefined}
      />
      {open && (
        <div
          className="z-[9999] overflow-y-auto bg-white border border-[#C4A252] shadow-2xl"
          style={popupStyle}
          onMouseDown={(e) => { e.preventDefault(); clearTimeout(blurTimer.current); }}
          data-testid={testid ? `${testid}-list` : undefined}
        >
          <button
            type="button"
            onClick={() => commit(null)}
            className="w-full text-left px-2 py-1.5 text-[11px] uppercase tracking-widest text-[#8B1C22] hover:bg-[#FEE2E2] border-b border-[#F5F5F5] bg-white sticky top-0 z-10"
          >
            — Ask Accountant —
          </button>
          {results.length === 0 ? (
            <div className="px-2 py-3 text-[11px] text-[#737373] text-center">No matches</div>
          ) : results.map((a, i) => (
            <button
              key={a.code}
              type="button"
              onClick={() => commit(a)}
              onMouseEnter={() => setCursor(i)}
              className={`w-full text-left px-2 py-1.5 text-xs ${i === cursor ? "bg-[#FDFBF7]" : "bg-white"} hover:bg-[#FDFBF7] border-b border-[#F5F5F5] last:border-b-0`}
            >
              <div className="font-medium text-[#0A0A0A]">
                <span className="text-[#8B1C22]">{a.code}</span> · {a.name}
              </div>
              <div className="text-[9px] uppercase tracking-widest text-[#737373]">
                {a.type}{(a.detail_type || a.subtype) ? ` · ${a.detail_type || a.subtype}` : ""}
              </div>
            </button>
          ))}
          {onCreateNew && (
            <button
              type="button"
              onClick={() => { setOpen(false); onCreateNew(query.trim()); }}
              className="w-full text-left px-2 py-1.5 text-[11px] uppercase tracking-widest text-[#8B1C22] bg-[#FDFBF7] hover:bg-[#FEE2E2] border-t border-[#C4A252] flex items-center gap-1 sticky bottom-0"
              data-testid={testid ? `${testid}-new` : undefined}
            >
              <span className="text-sm leading-none">+</span> Add new account…
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const SOURCE_BADGE = {
  rule: { label: "RULE", cls: "bg-[#DCFCE7] text-[#166534] border-[#166534]" },
  heuristic: { label: "AUTO", cls: "bg-[#DBEAFE] text-[#1E40AF] border-[#1E40AF]" },
  ai: { label: "AI", cls: "bg-[#EDE9FE] text-[#5B21B6] border-[#5B21B6]" },
  ai_pending: { label: "AI ?", cls: "bg-[#FEF3C7] text-[#78350F] border-[#C4A252]" },
  ai_approved: { label: "AI ✓", cls: "bg-[#DCFCE7] text-[#166534] border-[#166534]" },
  manual: { label: "MANUAL", cls: "bg-[#FEF3C7] text-[#78350F] border-[#C4A252]" },
  default: { label: "GUESS", cls: "bg-[#F3F4F6] text-[#404040] border-[#A3A3A3]" },
};

// Transaction-flow buckets — ordered top-to-bottom the way the accountant
// reads a statement: money-in first, then money-out grouped by nature.
// `section` matches what `bank_statement_parser.py` emits per txn.
const FLOW_BUCKETS = [
  { key: "deposits",           sections: ["deposits"],           dot: "#15803D", tone: "text-[#15803D]", labelEn: "Deposits",       labelEs: "Depósitos" },
  { key: "card_atm",           sections: ["atm_debit"],          dot: "#1E40AF", tone: "text-[#1E40AF]", labelEn: "Card & ATM",     labelEs: "Tarjeta / ATM" },
  { key: "other_expenses",     sections: ["other_subtractions"], dot: "#8B1C22", tone: "text-[#8B1C22]", labelEn: "Other Expenses", labelEs: "Otros gastos" },
  { key: "bank_fees",          sections: ["service_fees"],       dot: "#78350F", tone: "text-[#78350F]", labelEn: "Bank Fees",      labelEs: "Cargos bancarios" },
  { key: "other",              sections: [null, undefined, ""],  dot: "#737373", tone: "text-[#737373]", labelEn: "Other",          labelEs: "Otros" },
];
const _SECTION_TO_BUCKET = FLOW_BUCKETS.reduce((acc, b) => {
  b.sections.forEach((s) => { acc[s ?? "__none__"] = b.key; });
  return acc;
}, {});
const bucketForTxn = (t) => _SECTION_TO_BUCKET[t?.section ?? "__none__"] || "other";

const StatementsTab = ({ book, accounts = [], lang, reloadAccounts }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null); // full detail on view
  // Statement-list filters
  const [listQuery, setListQuery] = useState("");
  const [listFrom, setListFrom] = useState("");
  const [listTo, setListTo] = useState("");
  const [listStatus, setListStatus] = useState("");
  const [postCash, setPostCash] = useState(""); // dropdown state for post modal
  const [showPost, setShowPost] = useState(false);
  const [importInfo, setImportInfo] = useState(null);
  const [batchProgress, setBatchProgress] = useState(null); // {total, done, results?}
  const [dragging, setDragging] = useState(false);
  const [bulkPostCash, setBulkPostCash] = useState("");
  const [showBulkPost, setShowBulkPost] = useState(false);
  const [refreshingBatch, setRefreshingBatch] = useState(false);
  const [autopostPct, setAutopostPct] = useState(Math.round(((book.ai_autopost_threshold ?? 0.9) * 100)));
  const [savingThreshold, setSavingThreshold] = useState(false);
  const [previewOn, setPreviewOn] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [highlightTxnId, setHighlightTxnId] = useState(null);
  const [pdfSearchToken, setPdfSearchToken] = useState("");
  // Which flow buckets are visible in the txn table. `null` = show all.
  const [flowFilter, setFlowFilter] = useState(null);
  // "+ Add new account" mini-modal state (fires from AccountCombobox)
  const [newAccountModal, setNewAccountModal] = useState(null); // { txnId, prefillName, from: 'txn'|'review', reviewKey? }
  const [newAcct, setNewAcct] = useState({ code: "", name: "", type: "expense" });
  const [savingAcct, setSavingAcct] = useState(false);

  // Fetch + release the PDF object URL when the preview pane opens/closes
  useEffect(() => {
    let objectUrl = null;
    let cancelled = false;
    (async () => {
      if (previewOn && selected?.pdf_available) {
        try {
          const res = await api.get(
            `/back-office/books/${book.id}/statements/${selected.id}/pdf`,
            { responseType: "blob" },
          );
          if (cancelled) return;
          objectUrl = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
          setPreviewUrl(objectUrl);
        } catch {
          if (!cancelled) toast.error(lang === "es" ? "No se pudo cargar el PDF" : "Failed to load PDF");
        }
      } else {
        setPreviewUrl(null);
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
    };
  }, [previewOn, selected?.id, selected?.pdf_available, book.id, lang]);

  // Debounced PATCH to persist the slider value
  useEffect(() => {
    const th = autopostPct / 100;
    if (Math.abs(th - (book.ai_autopost_threshold ?? 0.9)) < 0.001) return;
    const handle = setTimeout(async () => {
      setSavingThreshold(true);
      try {
        await api.patch(`/back-office/books/${book.id}`, { ai_autopost_threshold: th });
        book.ai_autopost_threshold = th; // keep local copy in sync
      } catch (err) {
        toast.error(err.response?.data?.detail || "Save threshold failed");
      } finally {
        setSavingThreshold(false);
      }
    }, 600);
    return () => clearTimeout(handle);
  }, [autopostPct, book]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/back-office/books/${book.id}/statements`);
      setItems(data);
    } catch {
      toast.error(lang === "es" ? "Error al cargar" : "Failed to load statements");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [book.id]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/back-office/books/${book.id}/import-address`);
        setImportInfo(data);
      } catch {
        // Panel silently hides if the endpoint fails
      }
    })();
  }, [book.id]);


  const onFile = async (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    e.target.value = "";
    await uploadMany(list);
  };

  const uploadMany = async (fileList) => {
    // Accept PDFs and .zip archives (server auto-expands zips to inner PDFs).
    // Anything else (.7z, .rar, images, etc.) is rejected client-side with a
    // clear message so the user isn't left staring at a generic 400.
    const ok = fileList.filter((f) => {
      const n = f.name.toLowerCase();
      return n.endsWith(".pdf") || n.endsWith(".zip");
    });
    const rejected = fileList.filter((f) => !ok.includes(f));
    if (rejected.length) {
      const bad = rejected.map((f) => f.name).slice(0, 3).join(", ");
      toast.error(lang === "es"
        ? `Solo PDF o ZIP. Rechazados: ${bad}${rejected.length > 3 ? "…" : ""}`
        : `PDF or ZIP only. Rejected: ${bad}${rejected.length > 3 ? "…" : ""}`);
    }
    if (!ok.length) {
      if (!rejected.length) toast.error(lang === "es" ? "Suelta uno o más PDFs" : "Drop one or more PDFs");
      return;
    }
    if (ok.length > 24) {
      toast.error(lang === "es" ? "Máximo 24 archivos por lote" : "Max 24 files per batch");
      return;
    }
    setBusy(true);
    // Single .pdf path — keep original UX + toast. Any .zip goes through batch.
    if (ok.length === 1 && ok[0].name.toLowerCase().endsWith(".pdf")) {
      try {
        const fd = new FormData();
        fd.append("file", ok[0]);
        const { data } = await api.post(`/back-office/books/${book.id}/statements/upload`, fd);
        toast.success(lang === "es" ? "Extracción exitosa" : `Extracted ${data.transaction_count} transactions`);
        setSelected(data);
        load();
      } catch (err) {
        toast.error(err.response?.data?.detail || "Upload failed");
      } finally {
        setBusy(false);
      }
      return;
    }
    const pdfs = ok;
    // Batch path (zip and/or multi-file)
    setBatchProgress({ total: pdfs.length, done: 0 });
    const tid = toast.loading(lang === "es"
      ? `Procesando ${pdfs.length} archivos…`
      : `Processing ${pdfs.length} files…`);
    try {
      const fd = new FormData();
      pdfs.forEach((f) => fd.append("files", f));
      const { data } = await api.post(`/back-office/books/${book.id}/statements/batch-upload`, fd);
      setBatchProgress({ total: data.count, done: data.count, results: data.results });
      toast.success(
        lang === "es"
          ? `${data.succeeded} de ${data.count} procesados${data.failed ? ` · ${data.failed} fallaron` : ""}`
          : `${data.succeeded} of ${data.count} processed${data.failed ? ` · ${data.failed} failed` : ""}`,
        { id: tid },
      );
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Batch upload failed", { id: tid });
    } finally {
      setBusy(false);
    }
  };

  const onDrop = async (e) => {
    e.preventDefault();
    setDragging(false);
    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length) await uploadMany(files);
  };

  /** Re-fetch each batch upload's current reconciliation badge. Useful after
   * the admin runs Apply Rules / AI Categorize on each so the batch panel
   * knows which ones now qualify for bulk post. */
  const refreshBatchBadges = async () => {
    if (!batchProgress?.results) return;
    setRefreshingBatch(true);
    try {
      const oks = batchProgress.results.filter((r) => r.ok && r.upload_id);
      const updates = await Promise.all(oks.map(async (r) => {
        try {
          const { data } = await api.get(`/back-office/books/${book.id}/statements/${r.upload_id}`);
          return {
            upload_id: r.upload_id,
            badge: data.reconciliation?.summary?.badge,
            posted_at: data.posted_at || null,
          };
        } catch {
          return { upload_id: r.upload_id, badge: null, posted_at: null };
        }
      }));
      const byId = Object.fromEntries(updates.map((u) => [u.upload_id, u]));
      setBatchProgress((prev) => ({
        ...prev,
        results: prev.results.map((r) =>
          r.ok && byId[r.upload_id]
            ? { ...r, badge: byId[r.upload_id].badge, posted_at: byId[r.upload_id].posted_at }
            : r),
      }));
    } finally {
      setRefreshingBatch(false);
    }
  };

  const bulkPost = async () => {
    if (!batchProgress?.results?.length || !bulkPostCash) return;
    const ids = batchProgress.results
      .filter((r) => r.ok && r.badge === "pass" && !r.posted_at)
      .map((r) => r.upload_id);
    if (!ids.length) {
      toast.error(lang === "es" ? "Nada listo para publicar" : "Nothing ready to post");
      return;
    }
    setBusy(true);
    const tid = toast.loading(lang === "es" ? `Publicando ${ids.length}…` : `Posting ${ids.length}…`);
    try {
      const { data } = await api.post(`/back-office/books/${book.id}/statements/bulk-post`, {
        upload_ids: ids,
        cash_account_code: bulkPostCash,
      });
      toast.success(
        lang === "es"
          ? `${data.succeeded}/${data.count} publicadas · ${data.total_posted} transacciones · ${data.total_skipped_duplicates} duplicadas omitidas`
          : `${data.succeeded}/${data.count} posted · ${data.total_posted} txns · ${data.total_skipped_duplicates} dupes skipped`,
        { id: tid },
      );
      setShowBulkPost(false);
      setBulkPostCash("");
      await refreshBatchBadges();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk post failed", { id: tid });
    } finally {
      setBusy(false);
    }
  };

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/back-office/books/${book.id}/statements/${id}`);
      setSelected(data);
    } catch { toast.error("Failed to open"); }
  };

  const remove = async (id) => {
    if (!confirm(lang === "es" ? "¿Eliminar este estado de cuenta?" : "Delete this statement?")) return;
    try {
      await api.delete(`/back-office/books/${book.id}/statements/${id}`);
      toast.success(lang === "es" ? "Eliminado" : "Deleted");
      if (selected?.id === id) setSelected(null);
      load();
    } catch { toast.error("Delete failed"); }
  };

  const downloadPdf = async () => {
    if (!selected) return;
    try {
      const res = await api.get(
        `/back-office/books/${book.id}/statements/${selected.id}/pdf`,
        { responseType: "blob" },
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = selected.filename || "statement.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const detail = err.response?.status === 410
        ? (lang === "es"
          ? "El PDF original no se guardó para esta subida (anterior a Feb 2026)"
          : "Original PDF was not stored for this upload (uploaded before Feb 2026)")
        : (err.response?.data?.detail || "Download failed");
      toast.error(detail);
    }
  };

  const downloadFinalReport = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const res = await api.get(
        `/back-office/books/${book.id}/statements/${selected.id}/final-report.pdf`,
        { responseType: "blob" },
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      const base = (selected.filename || "statement").replace(/\.pdf$/i, "");
      a.download = `reconciliation_${base}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Report generation failed");
    } finally {
      setBusy(false);
    }
  };

  const approveReconciliation = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const { data } = await api.post(
        `/back-office/books/${book.id}/statements/${selected.id}/approve-reconciliation`,
      );
      toast.success(lang === "es" ? "Reconciliación aprobada" : "Reconciliation approved");
      setSelected((prev) => prev ? {
        ...prev,
        reconciliation_approved_at: data.approved_at,
        reconciliation_approved_by: data.approved_by,
      } : prev);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Approve failed");
    } finally {
      setBusy(false);
    }
  };

  const applyRules = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/back-office/books/${book.id}/statements/${selected.id}/apply-rules`);
      toast.success(
        lang === "es"
          ? `Reglas aplicadas · ${data.matched_by_rule} regla + ${data.matched_by_heuristic} auto · ${data.needs_review} para revisar`
          : `${data.matched_by_rule} matched by rule · ${data.matched_by_heuristic} by heuristic · ${data.needs_review} need review`,
      );
      await openDetail(selected.id);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Apply rules failed");
    } finally {
      setBusy(false);
    }
  };

  const aiCategorize = async () => {
    if (!selected) return;
    setBusy(true);
    const tid = toast.loading(lang === "es" ? "GPT-5 categorizando…" : "GPT-5 categorizing…");
    try {
      const { data } = await api.post(`/back-office/books/${book.id}/statements/${selected.id}/ai-categorize`);
      toast.success(
        lang === "es"
          ? `IA: ${data.auto_posted} auto-publicadas · ${data.queued_for_review} en revisión · ${data.no_match} sin coincidencia`
          : `AI: ${data.auto_posted} auto-posted · ${data.queued_for_review} queued · ${data.no_match} no match`,
        { id: tid },
      );
      await openDetail(selected.id);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI categorize failed", { id: tid });
    } finally {
      setBusy(false);
    }
  };

  const patchTxn = async (txnId, category_account_code) => {
    try {
      await api.patch(`/back-office/books/${book.id}/statements/${selected.id}/transactions/${txnId}`, {
        category_account_code,
      });
      // Optimistic in-place update
      setSelected((prev) => ({
        ...prev,
        transactions: prev.transactions.map((t) =>
          t.id === txnId
            ? { ...t, category_account_code, category_source: "manual", status: "categorized" }
            : t,
        ),
      }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    }
  };

  // Persist an arbitrary txn field (subaccount / notes) without touching the
  // bank amount — critical rule: reconciliation math must never be mutated.
  const patchTxnField = async (txnId, patch) => {
    try {
      await api.patch(`/back-office/books/${book.id}/statements/${selected.id}/transactions/${txnId}`, patch);
      setSelected((prev) => ({
        ...prev,
        transactions: prev.transactions.map((t) => t.id === txnId ? { ...t, ...patch } : t),
      }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    }
  };

  // Open "New account" modal with a suggested code (next available in same type).
  const openNewAccountModal = (context, prefillName = "") => {
    const inType = "expense";
    const nextCode = (() => {
      const codes = (accounts || [])
        .filter((a) => a.type === inType)
        .map((a) => parseInt(a.code, 10))
        .filter((n) => !isNaN(n))
        .sort((a, b) => a - b);
      if (codes.length === 0) return "6000";
      // Next 10-increment above the highest — mirrors common CoA convention
      return String(codes[codes.length - 1] + 10);
    })();
    setNewAcct({ code: nextCode, name: prefillName, type: inType });
    setNewAccountModal(context);
  };

  const saveNewAccount = async () => {
    const code = (newAcct.code || "").trim();
    const name = (newAcct.name || "").trim();
    if (!/^\d{3,6}$/.test(code)) { toast.error(lang === "es" ? "Código: 3–6 dígitos" : "Code must be 3–6 digits"); return; }
    if (!name) { toast.error(lang === "es" ? "Nombre requerido" : "Name required"); return; }
    setSavingAcct(true);
    try {
      const { data } = await api.post(`/back-office/books/${book.id}/accounts`, {
        code, name, type: newAcct.type,
      });
      toast.success(lang === "es" ? `Cuenta ${data.code} creada` : `Account ${data.code} created`);
      if (reloadAccounts) await reloadAccounts();
      // Auto-pick this new account on the txn that triggered the modal
      const ctx = newAccountModal;
      if (ctx?.from === "txn" && ctx.txnId) {
        await patchTxn(ctx.txnId, data.code);
      }
      setNewAccountModal(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSavingAcct(false);
    }
  };

  const toggleDupeOverride = async (t) => {
    const nextState = !t.dupe_override;
    try {
      await api.patch(`/back-office/books/${book.id}/statements/${selected.id}/transactions/${t.id}`, {
        dupe_override: nextState,
      });
      setSelected((prev) => ({
        ...prev,
        transactions: prev.transactions.map((x) =>
          x.id === t.id ? { ...x, dupe_override: nextState } : x,
        ),
      }));
      toast.success(
        nextState
          ? (lang === "es" ? "Override activado · se publicará como duplicado" : "Override on · will post as duplicate")
          : (lang === "es" ? "Override desactivado · se omitirá" : "Override off · will be skipped"),
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Toggle failed");
    }
  };

  /**
   * Highlight a parsed txn in the PDF preview pane. Pulls the strongest
   * search token from the description (longest alphanumeric run ≥ 4 chars,
   * skipping generic words like "TRANSFER" / "PAYMENT" / "ONLINE") and
   * feeds it to the browser's built-in PDF viewer via the `#search=` URL
   * fragment (Chrome/Edge/PDFium honor this). Also flashes the row.
   */
  const highlightInPdf = (t) => {
    if (!selected?.pdf_available) return;
    setPreviewOn(true);
    setHighlightTxnId(t.id);
    setTimeout(() => setHighlightTxnId((cur) => (cur === t.id ? null : cur)), 1400);

    const skip = new Set([
      "ONLINE", "TRANSFER", "PAYMENT", "PAYMENTS", "DEPOSIT", "DEBIT",
      "CREDIT", "PURCHASE", "PURCHASES", "ACH", "POS", "REF", "REFERENCE",
      "FROM", "TO", "AND", "THE", "FOR", "INC", "LLC", "COM", "BANK",
    ]);
    const tokens = (t.description || "")
      .toUpperCase()
      .split(/[^A-Z0-9]+/)
      .filter((w) => w.length >= 4 && !skip.has(w))
      .sort((a, b) => b.length - a.length);
    setPdfSearchToken(tokens[0] || (t.description || "").trim().split(/\s+/)[0] || "");
  };

  const saveAsRule = async (t) => {
    if (!t.category_account_code) {
      toast.error(lang === "es" ? "Categoriza primero" : "Categorize the transaction first");
      return;
    }
    try {
      const { data } = await api.post(`/back-office/books/${book.id}/statements/${selected.id}/save-rule`, {
        txn_id: t.id,
        category_account_code: t.category_account_code,
        match_type: "contains",
      });
      toast.success(lang === "es" ? `Regla creada: "${data.pattern}"` : `Rule saved: "${data.pattern}"`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save rule failed");
    }
  };

  const postToRegister = async () => {
    if (!selected || !postCash) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/back-office/books/${book.id}/statements/${selected.id}/post`, {
        cash_account_code: postCash,
      });
      toast.success(
        lang === "es"
          ? `${data.posted_count} publicadas${data.forced_duplicates ? ` (incluidas ${data.forced_duplicates} duplicadas forzadas)` : ""}${data.skipped_duplicates ? ` · ${data.skipped_duplicates} omitidas (duplicadas)` : ""}`
          : `Posted ${data.posted_count}${data.forced_duplicates ? ` (incl. ${data.forced_duplicates} forced dupes)` : ""}${data.skipped_duplicates ? ` · ${data.skipped_duplicates} skipped (duplicates)` : ""}`,
      );
      setShowPost(false);
      setPostCash("");
      await openDetail(selected.id);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Post failed");
    } finally {
      setBusy(false);
    }
  };

  const unpost = async () => {
    if (!selected) return;
    if (!confirm(lang === "es"
      ? "¿Deshacer la publicación? Se eliminarán las transacciones del registro."
      : "Unpost this statement? Every posted transaction will be deleted from the register.")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/back-office/books/${book.id}/statements/${selected.id}/unpost`);
      toast.success(lang === "es" ? `${data.deleted_count} eliminadas` : `${data.deleted_count} deleted`);
      await openDetail(selected.id);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Unpost failed");
    } finally {
      setBusy(false);
    }
  };


  // Full chart of accounts — every active, non-header account so the accountant
  // can assign ANY category (asset/liability/equity/income/cogs/expense).
  const categoryAccounts = useMemo(
    () => (accounts || [])
      .filter((a) => a.is_active !== false && !a.is_header)
      .sort((a, b) => a.code.localeCompare(b.code)),
    [accounts],
  );

  // Cash-account options for Post to Register (checking/savings/CC live under asset or liability)
  const cashAccounts = useMemo(
    () => (accounts || [])
      .filter((a) => a.is_active !== false && !a.is_header && ["asset", "liability"].includes(a.type))
      .sort((a, b) => a.code.localeCompare(b.code)),
    [accounts],
  );

  const rollup = useMemo(() => {
    const t = selected?.transactions || [];
    return {
      total: t.length,
      review: t.filter((x) => !x.category_account_code || x.status === "needs_review").length,
      rule: t.filter((x) => x.category_source === "rule").length,
      heuristic: t.filter((x) => x.category_source === "heuristic").length,
      ai: t.filter((x) => x.category_source === "ai").length,
      ai_pending: t.filter((x) => x.category_source === "ai_pending").length,
      manual: t.filter((x) => x.category_source === "manual").length,
      duplicates: t.filter((x) => x.duplicate_of && !x.dupe_override).length,
      dupes_forced: t.filter((x) => x.duplicate_of && x.dupe_override).length,
    };
  }, [selected]);

  // Distinct non-empty subaccount + notes values in this statement — used as
  // typeahead suggestions so accountants don't have to retype "Site A" on
  // every txn from the same job.
  const knownSubaccounts = useMemo(() => {
    const set = new Set();
    (selected?.transactions || []).forEach((t) => {
      const v = (t.subaccount || "").trim();
      if (v) set.add(v);
    });
    return Array.from(set).sort();
  }, [selected]);
  const knownNotes = useMemo(() => {
    const set = new Set();
    (selected?.transactions || []).forEach((t) => {
      const v = (t.notes || "").trim();
      if (v) set.add(v);
    });
    return Array.from(set).sort();
  }, [selected]);

  // Statement-list filtering — client-side over the already-loaded rows.
  const filteredItems = useMemo(() => {
    const q = listQuery.trim().toLowerCase();
    return (items || []).filter((s) => {
      if (q) {
        const hay = `${s.filename || ""} ${s.bank_label || ""} ${s.account_holder || ""} ${s.account_number_masked || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      const uploaded = (s.uploaded_at || "").slice(0, 10);
      if (listFrom && uploaded && uploaded < listFrom) return false;
      if (listTo && uploaded && uploaded > listTo) return false;
      if (listStatus) {
        if (listStatus === "posted") { if (!s.posted_at) return false; }
        else if (listStatus === "approved") { if (!s.reconciliation_approved_at) return false; }
        else if (listStatus !== s.recon_summary?.badge) return false;
      }
      return true;
    });
  }, [items, listQuery, listFrom, listTo, listStatus]);

  // Group parsed txns by flow bucket + preserve statement flow order.
  // Empty buckets are dropped so we don't render dead sections in the sidebar.
  const flowGroups = useMemo(() => {
    const txns = selected?.transactions || [];
    const byKey = {};
    for (const b of FLOW_BUCKETS) byKey[b.key] = { ...b, txns: [], total: 0 };
    for (const t of txns) {
      const k = bucketForTxn(t);
      byKey[k].txns.push(t);
      byKey[k].total += Number(t.amount || 0);
    }
    return FLOW_BUCKETS
      .map((b) => byKey[b.key])
      .filter((g) => g.txns.length > 0);
  }, [selected]);

  // Reset flow filter whenever a different statement is opened.
  useEffect(() => { setFlowFilter(null); }, [selected?.id]);

  const isFlowActive = (key) => flowFilter === null || flowFilter.has(key);
  const toggleFlow = (key) => {
    setFlowFilter((prev) => {
      // From "all" → clicking one starts a selection with just that one.
      if (prev === null) {
        const s = new Set([key]);
        return s;
      }
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      // Empty selection or all-selected collapses back to "all" for clarity.
      if (next.size === 0 || next.size === flowGroups.length) return null;
      return next;
    });
  };
  const clearFlowFilter = () => setFlowFilter(null);

  const visibleFlowGroups = flowGroups.filter((g) => isFlowActive(g.key));

  return (
    <div className="space-y-6" data-testid="statements-tab">
      {/* Upload row */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={`border border-dashed p-6 text-center transition-colors ${dragging ? "border-[#8B1C22] bg-[#FEE2E2]" : "border-[#C4A252] bg-[#FDFBF7]"}`}
        data-testid="stmt-upload-zone"
      >
        <FileText className="w-8 h-8 text-[#C4A252] mx-auto mb-3" />
        <div className="rn3-eyebrow text-[#8B1C22] mb-1">{lang === "es" ? "Estados de cuenta bancarios" : "Bank statements"}</div>
        <p className="text-xs text-[#404040] mb-4 max-w-md mx-auto">
          {lang === "es"
            ? "Sube PDFs (o un ZIP con PDFs) de Bank of America, Chase, Wells Fargo, Truist, Citibank o Capital One. Arrastra hasta 24 archivos a la vez. Detectamos el banco automáticamente, extraemos transacciones, aplicamos tus reglas y encolamos el resto para revisión."
            : "Upload PDFs (or a ZIP full of PDFs) from Bank of America, Chase, Wells Fargo, Truist, Citibank, or Capital One. Drag up to 24 files at once. We auto-detect the bank, extract every transaction, apply your saved rules, and queue the rest for accountant review."}
        </p>
        <label className="rn3-btn-primary cursor-pointer inline-flex" data-testid="stmt-upload-btn">
          <Upload className="w-4 h-4" /> {busy ? "…" : (lang === "es" ? "Subir PDFs" : "Upload PDFs")}
          <input type="file" accept=".pdf,.zip,application/pdf,application/zip" multiple onChange={onFile} className="hidden" disabled={busy} data-testid="stmt-upload-input" />
        </label>
        <div className="text-[10px] text-[#737373] mt-2">
          {lang === "es" ? "o arrastra los archivos aquí" : "or drag files here"}
        </div>
      </div>

      {/* Batch progress + per-file results */}
      {batchProgress?.results && (
        <div className="border border-[#E5E5E5] bg-white" data-testid="stmt-batch-results">
          <div className="px-4 py-2 border-b border-[#E5E5E5] bg-[#FAFAF7] flex flex-wrap items-center justify-between gap-2">
            <div className="rn3-eyebrow text-[#404040]">
              {lang === "es" ? "Resultado del lote" : "Batch result"}
              <span className="ml-2 text-[#737373]">{batchProgress.done}/{batchProgress.total}</span>
              {(() => {
                const passReady = batchProgress.results.filter((r) => r.ok && r.badge === "pass" && !r.posted_at).length;
                return passReady > 0 ? (
                  <span className="ml-3 text-[10px] text-[#166534] uppercase tracking-widest border border-[#166534] px-1.5 py-0.5">
                    {passReady} {lang === "es" ? "listas" : "ready"}
                  </span>
                ) : null;
              })()}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={refreshBatchBadges}
                disabled={refreshingBatch}
                className="border border-[#C4A252] text-[#8B1C22] hover:bg-[#FDFBF7] inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-1 transition-colors disabled:opacity-50"
                data-testid="stmt-batch-refresh-btn"
              >
                {refreshingBatch ? "…" : (lang === "es" ? "Actualizar" : "Refresh")}
              </button>
              {(() => {
                const passReady = batchProgress.results.filter((r) => r.ok && r.badge === "pass" && !r.posted_at).length;
                return (
                  <button
                    onClick={() => { setBulkPostCash(""); setShowBulkPost(true); }}
                    disabled={passReady === 0 || busy}
                    className="rn3-btn-primary inline-flex items-center gap-1 text-[10px] px-2 py-1 disabled:opacity-40 disabled:cursor-not-allowed"
                    title={passReady === 0
                      ? (lang === "es" ? "Ninguna cuadra aún — categoriza y actualiza" : "Nothing reconciled yet — categorize and refresh")
                      : ""}
                    data-testid="stmt-batch-post-all-btn"
                  >
                    <Send className="w-3 h-3" />
                    {lang === "es"
                      ? `Publicar ${passReady} cuadradas`
                      : `Post ${passReady} reconciled`}
                  </button>
                );
              })()}
              <button onClick={() => setBatchProgress(null)} className="text-[#737373] hover:text-[#8B1C22] text-xs">✕</button>
            </div>
          </div>
          <table className="w-full text-xs">
            <tbody>
              {batchProgress.results.map((r, i) => (
                <tr key={i} className="border-b border-[#F5F5F5]" data-testid={`stmt-batch-row-${i}`}>
                  <td className="py-1.5 px-3 w-6">
                    {r.ok
                      ? <CheckCircle2 className="w-3.5 h-3.5 text-[#15803D]" />
                      : <AlertTriangle className="w-3.5 h-3.5 text-[#8B1C22]" />}
                  </td>
                  <td className="py-1.5 px-3 flex-1 truncate">{r.filename}</td>
                  <td className="py-1.5 px-3 text-right whitespace-nowrap">
                    {r.ok
                      ? <span className="text-[#404040]">{r.transaction_count} txns · <span className="text-[#737373]">{r.bank_label || "?"}</span></span>
                      : <span className="text-[#8B1C22] truncate max-w-[240px] inline-block">{r.error}</span>}
                  </td>
                  <td className="py-1.5 px-3 text-right">
                    {r.ok && r.badge && (
                      <span className={`text-[9px] uppercase tracking-widest border px-1.5 py-0.5 ${
                        r.posted_at
                          ? "border-[#166534] bg-[#DCFCE7] text-[#166534]"
                          : r.badge === "pass"
                            ? "border-[#166534] bg-[#DCFCE7] text-[#166534]"
                            : r.badge === "review"
                              ? "border-[#C4A252] bg-[#FEF3C7] text-[#78350F]"
                              : "border-[#8B1C22] bg-[#FEE2E2] text-[#8B1C22]"
                      }`}>
                        {r.posted_at ? (lang === "es" ? "PUBLICADA" : "POSTED") : r.badge.toUpperCase()}
                      </span>
                    )}
                  </td>
                  <td className="py-1.5 px-3 text-right">
                    {r.ok && r.upload_id && (
                      <button
                        onClick={() => openDetail(r.upload_id)}
                        className="text-[10px] uppercase tracking-widest text-[#8B1C22] underline"
                        data-testid={`stmt-batch-open-${i}`}
                      >
                        {lang === "es" ? "Abrir" : "Open"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Categorization Rules (Rules Registry) */}
      <RulesPanel book={book} accounts={accounts} lang={lang} />

      {/* Auto-import email address (per-book) */}      {importInfo?.import_address && (
        <div className="border border-[#E5E5E5] bg-white px-4 py-3" data-testid="stmt-import-address-panel">
          <div className="flex flex-wrap items-center gap-3">
            <Mail className="w-4 h-4 text-[#C4A252]" />
            <div className="flex-1 min-w-0">
              <div className="rn3-eyebrow text-[#404040]">
                {lang === "es" ? "Importación automática por correo" : "Auto-import via email"}
              </div>
              <p className="text-[10px] text-[#737373] mt-0.5">
                {lang === "es"
                  ? "Reenvía el PDF del extracto a esta dirección. Lo analizamos y lo encolamos automáticamente para revisión."
                  : "Forward any statement PDF to this address. We parse it and queue it for review automatically."}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <code className="text-[11px] bg-[#FDFBF7] border border-[#E5E5E5] px-2 py-1 tabular-nums select-all" data-testid="stmt-import-address">
                {importInfo.import_address}
              </code>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(importInfo.import_address);
                  toast.success(lang === "es" ? "Dirección copiada" : "Address copied");
                }}
                className="text-[10px] uppercase tracking-widest border border-[#C4A252] px-2 py-1 text-[#8B1C22] hover:bg-[#FDFBF7]"
                data-testid="stmt-import-address-copy"
              >
                {lang === "es" ? "Copiar" : "Copy"}
              </button>
            </div>
          </div>
          {!importInfo.configured && (
            <div className="mt-2 text-[10px] text-[#8B1C22] flex items-start gap-1">
              <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
              <span>
                {lang === "es"
                  ? "El webhook de correo entrante aún no está configurado (requiere DNS + secret). Guarda esta dirección para cuando esté listo."
                  : "Inbound-email webhook not yet configured (needs DNS + secret). Save this address for when it's live."}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Review Queue (Phase D) */}
      <ReviewQueuePanel
        book={book}
        accounts={accounts}
        lang={lang}
        onApproved={async () => {
          if (selected) await openDetail(selected.id);
          load();
        }}
      />

      {/* List */}
      <div className="border border-[#E5E5E5]">
        {/* Filter bar for the statements list */}
        <div className="px-3 py-2 border-b border-[#E5E5E5] bg-[#FDFBF7] flex flex-wrap gap-2 items-center">
          <input
            type="text"
            value={listQuery}
            onChange={(e) => setListQuery(e.target.value)}
            placeholder={lang === "es" ? "Buscar archivo, banco, titular, cuenta…" : "Search file, bank, holder, account…"}
            className="border border-[#E5E5E5] text-xs py-1 px-2 bg-white flex-1 min-w-[220px]"
            data-testid="stmt-list-search"
          />
          <input
            type="date"
            value={listFrom}
            onChange={(e) => setListFrom(e.target.value)}
            className="border border-[#E5E5E5] text-xs py-1 px-2 bg-white tabular-nums"
            title={lang === "es" ? "Desde" : "From"}
            data-testid="stmt-list-from"
          />
          <input
            type="date"
            value={listTo}
            onChange={(e) => setListTo(e.target.value)}
            className="border border-[#E5E5E5] text-xs py-1 px-2 bg-white tabular-nums"
            title={lang === "es" ? "Hasta" : "To"}
            data-testid="stmt-list-to"
          />
          <select
            value={listStatus}
            onChange={(e) => setListStatus(e.target.value)}
            className="border border-[#E5E5E5] text-xs py-1 px-2 bg-white"
            data-testid="stmt-list-status"
          >
            <option value="">{lang === "es" ? "Todos" : "All statuses"}</option>
            <option value="pass">{lang === "es" ? "Cuadra" : "Balanced / Pass"}</option>
            <option value="review">{lang === "es" ? "Por revisar" : "Needs review"}</option>
            <option value="variance">{lang === "es" ? "Variación" : "Variance"}</option>
            <option value="posted">{lang === "es" ? "Publicado" : "Posted"}</option>
            <option value="approved">{lang === "es" ? "Aprobado" : "Approved"}</option>
          </select>
          {(listQuery || listFrom || listTo || listStatus) && (
            <button
              onClick={() => { setListQuery(""); setListFrom(""); setListTo(""); setListStatus(""); }}
              className="text-[10px] uppercase tracking-widest text-[#8B1C22] hover:underline px-2"
              data-testid="stmt-list-clear"
            >
              {lang === "es" ? "Limpiar" : "Clear"}
            </button>
          )}
          <span className="text-[10px] text-[#737373] tabular-nums ml-auto">
            {filteredItems.length} / {items.length}
          </span>
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-[#737373]">{lang === "es" ? "Cargando…" : "Loading…"}</div>
        ) : filteredItems.length === 0 ? (
          <div className="p-10 text-center text-sm text-[#737373]" data-testid="stmt-empty">
            {items.length === 0
              ? (lang === "es" ? "No hay estados de cuenta subidos" : "No statements uploaded yet")
              : (lang === "es" ? "Ningún estado coincide con los filtros" : "No statements match your filters")}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#FDFBF7]">
              <tr className="text-left border-b border-[#E5E5E5]">
                <th className="py-2 px-3 rn3-eyebrow">{lang === "es" ? "Archivo" : "File"}</th>
                <th className="py-2 px-3 rn3-eyebrow">{lang === "es" ? "Subido" : "Uploaded"}</th>
                <th className="py-2 px-3 rn3-eyebrow text-right">{lang === "es" ? "Transacciones" : "Txns"}</th>
                <th className="py-2 px-3 rn3-eyebrow">{lang === "es" ? "Reconcilia" : "Reconciles"}</th>
                <th className="py-2 px-3 rn3-eyebrow">{lang === "es" ? "Estado" : "Status"}</th>
                <th className="py-2 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((s) => (
                <tr key={s.id} className="border-b border-[#F5F5F5] hover:bg-[#FDFBF7]/50" data-testid={`stmt-row-${s.id}`}>
                  <td className="py-2 px-3">
                    <button onClick={() => openDetail(s.id)} className="text-[#8B1C22] underline text-left">
                      {s.filename}
                    </button>
                    {s.bank_label && (
                      <div className="text-[9px] uppercase tracking-widest text-[#737373] mt-0.5" data-testid={`stmt-bank-${s.id}`}>
                        {s.bank_label}
                      </div>
                    )}
                  </td>
                  <td className="py-2 px-3 text-xs text-[#737373]">{(s.uploaded_at || "").slice(0, 10)}</td>
                  <td className="py-2 px-3 text-right font-medium">{s.transaction_count}</td>
                  <td className="py-2 px-3" data-testid={`stmt-recon-${s.id}`}>
                    {s.recon_summary?.badge === "pass" ? (
                      <span className="text-[#15803D] inline-flex items-center gap-1 text-xs" title={lang === "es" ? "Cuadra y todo categorizado" : "Reconciles + fully categorized"}>
                        <CheckCircle2 className="w-3 h-3" /> {lang === "es" ? "Cuadra" : "Pass"}
                      </span>
                    ) : s.recon_summary?.badge === "review" ? (
                      <span className="text-[#C4A252] inline-flex items-center gap-1 text-xs" title={lang === "es" ? "Cuadra pero falta revisar" : "Reconciles, needs review"}>
                        <AlertTriangle className="w-3 h-3" /> {s.recon_summary.total_count - s.recon_summary.categorized_count} {lang === "es" ? "por revisar" : "to review"}
                      </span>
                    ) : (
                      <span className="text-[#8B1C22] inline-flex items-center gap-1 text-xs" title={lang === "es" ? "Los totales de control no cuadran" : "Control totals do not reconcile"}>
                        <AlertTriangle className="w-3 h-3" /> {lang === "es" ? "Variación" : "Variance"}
                      </span>
                    )}
                  </td>
                  <td className="py-2 px-3">
                    <span className="text-[10px] uppercase tracking-widest border border-[#C4A252] px-2 py-0.5">{s.status}</span>
                  </td>
                  <td className="py-2 px-3 text-right">
                    <button onClick={() => remove(s.id)} className="text-[#737373] hover:text-[#8B1C22]" data-testid={`stmt-delete-${s.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Detail panel */}
      {selected && (
        <div className="border border-[#E5E5E5] bg-white" data-testid="stmt-detail">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E5E5] bg-[#FDFBF7]">
            <div>
              <div className="rn3-eyebrow">{lang === "es" ? "Detalle" : "Statement detail"}</div>
              <div className="font-serif-display text-lg">{selected.filename}</div>
              {selected.bank_label && (
                <div className="text-[9px] uppercase tracking-widest text-[#737373] mt-0.5">
                  {selected.bank_label}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={applyRules}
                disabled={busy}
                className="rn3-btn-gold inline-flex items-center gap-1 text-xs"
                data-testid="stmt-apply-rules-btn"
              >
                <Wand2 className="w-3 h-3" />
                {busy ? "…" : (lang === "es" ? "Aplicar reglas" : "Apply rules")}
              </button>
              <button
                onClick={aiCategorize}
                disabled={busy}
                className="rn3-btn-primary inline-flex items-center gap-1 text-xs"
                data-testid="stmt-ai-categorize-btn"
              >
                <Sparkles className="w-3 h-3" />
                {busy ? "…" : (lang === "es" ? "IA GPT-5" : "AI GPT-5")}
              </button>
              {/* Per-book AI auto-post confidence slider */}
              <div className="hidden md:flex items-center gap-2 border border-[#E5E5E5] bg-white px-2 py-1" title={lang === "es"
                  ? "Umbral de auto-publicación de IA: 100% = revisar todo, 0% = confiar en todo"
                  : "AI auto-post threshold: 100% = review every pick, 0% = trust every pick"} data-testid="stmt-autopost-slider-wrap">
                <label className="text-[9px] uppercase tracking-widest text-[#737373]">
                  {lang === "es" ? "Auto ≥" : "Auto ≥"}
                </label>
                <input
                  type="range"
                  min={50}
                  max={100}
                  step={1}
                  value={autopostPct}
                  onChange={(e) => setAutopostPct(Number(e.target.value))}
                  className="accent-[#8B1C22] w-24"
                  data-testid="stmt-autopost-slider"
                />
                <span className="text-[10px] tabular-nums text-[#404040] w-8">{autopostPct}%</span>
                {savingThreshold && <span className="text-[9px] text-[#C4A252]">…</span>}
              </div>
              {selected.pdf_available && (
                <>
                  <button
                    onClick={() => setPreviewOn((o) => !o)}
                    className={`inline-flex items-center gap-1 text-xs px-3 py-1.5 uppercase tracking-widest transition-colors ${
                      previewOn
                        ? "bg-[#8B1C22] text-white border border-[#8B1C22]"
                        : "border border-[#C4A252] text-[#8B1C22] hover:bg-[#FDFBF7]"
                    }`}
                    title={lang === "es" ? "Previsualizar PDF" : "Preview PDF"}
                    data-testid="stmt-preview-btn"
                  >
                    {previewOn ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                    {lang === "es" ? "Vista" : "Preview"}
                  </button>
                  <button
                    onClick={downloadPdf}
                    disabled={busy}
                    className="border border-[#C4A252] text-[#8B1C22] hover:bg-[#FDFBF7] inline-flex items-center gap-1 text-xs px-3 py-1.5 uppercase tracking-widest transition-colors"
                    title={lang === "es" ? "Descargar PDF original" : "Download original PDF"}
                    data-testid="stmt-download-pdf-btn"
                  >
                    <Download className="w-3 h-3" />
                    PDF
                  </button>
                </>
              )}
              {selected.posted_at ? (
                <button
                  onClick={unpost}
                  disabled={busy}
                  className="border border-[#8B1C22] text-[#8B1C22] hover:bg-[#8B1C22] hover:text-white inline-flex items-center gap-1 text-xs px-3 py-1.5 uppercase tracking-widest transition-colors"
                  data-testid="stmt-unpost-btn"
                >
                  <Undo2 className="w-3 h-3" />
                  {busy ? "…" : (lang === "es" ? "Deshacer" : "Unpost")}
                </button>
              ) : (
                <button
                  onClick={() => {
                    setPostCash("");
                    setShowPost(true);
                  }}
                  disabled={busy || selected.reconciliation?.summary?.badge !== "pass"}
                  className="rn3-btn-primary inline-flex items-center gap-1 text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                  title={
                    selected.reconciliation?.summary?.badge !== "pass"
                      ? (lang === "es"
                        ? "El extracto debe estar totalmente conciliado y categorizado"
                        : "Statement must fully reconcile and every txn must be categorized")
                      : ""
                  }
                  data-testid="stmt-post-btn"
                >
                  <Send className="w-3 h-3" />
                  {lang === "es" ? "Publicar al registro" : "Post to register"}
                </button>
              )}
              <button onClick={() => setSelected(null)} className="text-[#737373] text-sm px-2">✕</button>
            </div>
          </div>

          {/* Posted-state banner */}
          {selected.posted_at && (() => {
            const pAcct = (accounts || []).find((x) => x.code === selected.posted_cash_account_code);
            const parts = [];
            parts.push(selected.posted_cash_account_code);
            if (pAcct?.name) parts.push(pAcct.name);
            if (pAcct?.nickname) parts.push(`"${pAcct.nickname}"`);
            if (pAcct?.detail_type) parts.push(pAcct.detail_type);
            const pLabel = parts.join(" · ");
            return (
              <div className="px-4 py-2 bg-[#DCFCE7] border-b border-[#166534] text-[11px] text-[#166534] flex items-center gap-2" data-testid="stmt-posted-banner">
                <CheckCircle2 className="w-3.5 h-3.5" />
                {lang === "es"
                  ? `Publicado al registro el ${(selected.posted_at || "").slice(0,10)} · ${selected.posted_count} transacciones · ${pLabel}`
                  : `Posted to register on ${(selected.posted_at || "").slice(0,10)} · ${selected.posted_count} transactions · ${pLabel}`}
              </div>
            );
          })()}

          <div className={previewOn ? "grid grid-cols-1 lg:grid-cols-2 gap-0 lg:divide-x lg:divide-[#E5E5E5]" : ""}>
            <div className={previewOn ? "lg:overflow-y-auto lg:max-h-[80vh]" : ""}>
          {/* Statement facts: Bank / Holder / Acct# / Period */}
          <div className="px-4 py-3 grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-3 text-xs border-b border-[#F5F5F5] bg-[#FDFBF7]" data-testid="stmt-facts">
            <div className="min-w-0">
              <div className="rn3-eyebrow">{lang === "es" ? "Banco" : "Bank"}</div>
              <div className="font-medium truncate" data-testid="stmt-fact-bank" title={selected.bank_label || ""}>{selected.bank_label || "—"}</div>
            </div>
            <div className="min-w-0">
              <div className="rn3-eyebrow">{lang === "es" ? "Titular" : "Account holder"}</div>
              <div className="font-medium truncate" data-testid="stmt-fact-holder" title={selected.account_holder || ""}>
                {selected.account_holder || "—"}
              </div>
            </div>
            <div className="min-w-0">
              <div className="rn3-eyebrow">{lang === "es" ? "Cuenta" : "Account #"}</div>
              <div className="font-medium tabular-nums truncate" data-testid="stmt-fact-acct">
                {selected.account_number_masked || "—"}
              </div>
            </div>
            <div className="min-w-0">
              <div className="rn3-eyebrow">{lang === "es" ? "Desde" : "Statement start"}</div>
              <div className="font-medium tabular-nums truncate" data-testid="stmt-fact-start">
                {selected.statement_start_date || "—"}
              </div>
            </div>
            <div className="min-w-0">
              <div className="rn3-eyebrow">{lang === "es" ? "Hasta" : "Statement end"}</div>
              <div className="font-medium tabular-nums truncate" data-testid="stmt-fact-end">
                {selected.statement_end_date || "—"}
              </div>
            </div>
          </div>

          {/* Reconciliation banner — BALANCED / OUT OF BALANCE with 5-line formula */}
          {(() => {
            const ct = selected.control_totals || {};
            const beg = Number(ct.beginning_balance || 0);
            const dep = Math.abs(Number(ct.deposits_total || 0));
            const atm = Math.abs(Number(ct.atm_debit_total || 0));
            const oth = Math.abs(Number(ct.other_subtractions_total || 0));
            const fees = Math.abs(Number(ct.service_fees_total || 0));
            const withdrawals = atm + oth;
            const calc = Math.round((beg + dep - withdrawals - fees) * 100) / 100;
            const end = Number(ct.ending_balance || 0);
            const variance = Math.round((end - calc) * 100) / 100;
            const balanced = Math.abs(variance) < 0.005;
            const approved = !!selected.reconciliation_approved_at;
            return (
              <div className="px-4 py-3 border-b border-[#F5F5F5]" data-testid="stmt-reconciliation-banner">
                <div className="flex flex-wrap items-stretch gap-4">
                  <div
                    className={`px-4 py-3 text-center flex-shrink-0 w-[200px] flex flex-col justify-center ${balanced ? "bg-[#DCFCE7] border border-[#166534]" : "bg-[#FEE2E2] border border-[#8B1C22]"}`}
                    data-testid="stmt-recon-status"
                  >
                    <div className={`font-serif-display text-lg leading-tight ${balanced ? "text-[#166534]" : "text-[#8B1C22]"}`}>
                      {balanced ? (lang === "es" ? "CUADRA" : "BALANCED") : (lang === "es" ? "FUERA DE BALANCE" : "OUT OF BALANCE")}
                    </div>
                    <div className={`text-[11px] uppercase tracking-widest mt-1 ${balanced ? "text-[#166534]" : "text-[#8B1C22]"}`} data-testid="stmt-recon-variance">
                      {fmt(Math.abs(variance))} {lang === "es" ? "VARIANZA" : "VARIANCE"}
                    </div>
                  </div>
                  <div className="flex-1 min-w-[260px] text-xs tabular-nums">
                    <div className="grid grid-cols-[1fr_auto] gap-x-6 gap-y-1">
                      <div className="text-[#404040]">{lang === "es" ? "Saldo inicial" : "Beginning balance"}</div>
                      <div className="text-right font-medium">{fmt(beg)}</div>
                      <div className="text-[#15803D]">+ {lang === "es" ? "Depósitos" : "Deposits"}</div>
                      <div className="text-right font-medium text-[#15803D]">{fmt(dep)}</div>
                      <div className="text-[#8B1C22]">− {lang === "es" ? "Retiros" : "Withdrawals"}</div>
                      <div className="text-right font-medium text-[#8B1C22]">{fmt(withdrawals)}</div>
                      <div className="text-[#78350F]">− {lang === "es" ? "Cargos" : "Fees"}</div>
                      <div className="text-right font-medium text-[#78350F]">{fmt(fees)}</div>
                      <div className="border-t border-[#E5E5E5] pt-1 font-bold">= {lang === "es" ? "Saldo calculado" : "Calculated ending"}</div>
                      <div className="border-t border-[#E5E5E5] pt-1 text-right font-bold">{fmt(calc)}</div>
                      <div className="text-[#404040]">{lang === "es" ? "Saldo del extracto" : "Statement ending"}</div>
                      <div className="text-right font-medium">{fmt(end)}</div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 w-full sm:w-[180px] flex-shrink-0">
                    {approved ? (
                      <div className="text-[10px] uppercase tracking-widest border border-[#166534] bg-[#DCFCE7] text-[#166534] px-2 py-1 text-center" data-testid="stmt-approved-badge">
                        <CheckCircle2 className="w-3 h-3 inline mr-1" />
                        {lang === "es" ? "APROBADO" : "APPROVED"}
                        <div className="text-[9px] mt-0.5 normal-case tracking-normal opacity-80">
                          {(selected.reconciliation_approved_at || "").slice(0, 10)}
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={approveReconciliation}
                        disabled={busy || !balanced}
                        className="rn3-btn-primary text-xs inline-flex items-center justify-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid="stmt-approve-recon-btn"
                        title={balanced ? "" : (lang === "es" ? "Debe cuadrar antes de aprobar" : "Must be balanced before approving")}
                      >
                        <Check className="w-3 h-3" />
                        {lang === "es" ? "Aprobar" : "Approve"}
                      </button>
                    )}
                    <button
                      onClick={downloadFinalReport}
                      disabled={busy}
                      className="border border-[#C4A252] text-[#8B1C22] hover:bg-[#FDFBF7] text-xs inline-flex items-center justify-center gap-1 px-3 py-1.5 uppercase tracking-widest transition-colors"
                      data-testid="stmt-final-report-btn"
                    >
                      <Download className="w-3 h-3" />
                      {lang === "es" ? "Informe" : "Report"}
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Control totals */}
          <div className="px-4 py-4 grid grid-cols-2 md:grid-cols-6 gap-3 text-xs border-b border-[#F5F5F5]">
            <div><div className="rn3-eyebrow">Beg</div><div className="font-medium">{fmt(selected.control_totals?.beginning_balance)}</div></div>
            <div><div className="rn3-eyebrow">Deposits</div><div className="font-medium text-[#15803D]">{fmt(selected.control_totals?.deposits_total)}</div></div>
            <div><div className="rn3-eyebrow">ATM/Debit</div><div className="font-medium text-[#8B1C22]">−{fmt(selected.control_totals?.atm_debit_total)}</div></div>
            <div><div className="rn3-eyebrow">Other −</div><div className="font-medium text-[#8B1C22]">−{fmt(selected.control_totals?.other_subtractions_total)}</div></div>
            <div><div className="rn3-eyebrow">Fees</div><div className="font-medium text-[#8B1C22]">−{fmt(selected.control_totals?.service_fees_total)}</div></div>
            <div><div className="rn3-eyebrow">End</div><div className="font-medium">{fmt(selected.control_totals?.ending_balance)}</div></div>
          </div>

          {/* Categorization rollup */}
          <div className="px-4 py-2 flex flex-wrap gap-3 text-[11px] text-[#404040] border-b border-[#F5F5F5] bg-[#FAFAF7]">
            <span><b>{rollup.total}</b> {lang === "es" ? "txns" : "txns"}</span>
            <span className="text-[#166534]"><b>{rollup.rule}</b> rule</span>
            <span className="text-[#1E40AF]"><b>{rollup.heuristic}</b> auto</span>
            <span className="text-[#5B21B6]"><b>{rollup.ai}</b> ai</span>
            <span className="text-[#C4A252]"><b>{rollup.ai_pending}</b> ai?</span>
            <span className="text-[#78350F]"><b>{rollup.manual}</b> manual</span>
            <span className="text-[#8B1C22]"><b>{rollup.review}</b> {lang === "es" ? "por revisar" : "need review"}</span>
            {rollup.duplicates > 0 && (
              <span className="text-[#8B1C22] font-bold" data-testid="stmt-dupe-rollup">
                <b>{rollup.duplicates}</b> {lang === "es" ? "duplicadas" : "duplicates"}
              </span>
            )}
            {rollup.dupes_forced > 0 && (
              <span className="text-[#166534] font-bold" data-testid="stmt-dupe-forced-rollup">
                <b>{rollup.dupes_forced}</b> {lang === "es" ? "duplicadas forzadas" : "forced dupes"}
              </span>
            )}
          </div>

          {/* Reconciliation (Phase E) */}
          {selected.reconciliation && (
            <div className="px-4 py-3 border-b border-[#F5F5F5]" data-testid="stmt-reconciliation">
              <div className="flex items-center gap-2 mb-2">
                <div className="rn3-eyebrow">{lang === "es" ? "Reconciliación" : "Reconciliation"}</div>
                {selected.reconciliation.summary.badge === "pass" ? (
                  <span className="text-[10px] uppercase tracking-widest border border-[#166534] text-[#166534] bg-[#DCFCE7] px-1.5 py-0.5">
                    {lang === "es" ? "CUADRA" : "PASS"}
                  </span>
                ) : selected.reconciliation.summary.badge === "review" ? (
                  <span className="text-[10px] uppercase tracking-widest border border-[#C4A252] text-[#78350F] bg-[#FEF3C7] px-1.5 py-0.5">
                    {lang === "es" ? "REVISAR" : "REVIEW"}
                  </span>
                ) : (
                  <span className="text-[10px] uppercase tracking-widest border border-[#8B1C22] text-[#8B1C22] bg-[#FEE2E2] px-1.5 py-0.5">
                    {lang === "es" ? "VARIACIÓN" : "VARIANCE"}
                  </span>
                )}
              </div>
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="text-left border-b border-[#E5E5E5]">
                    <th className="py-1 pr-3 rn3-eyebrow">{lang === "es" ? "Sección" : "Section"}</th>
                    <th className="py-1 px-3 rn3-eyebrow text-right">{lang === "es" ? "Extracto" : "Statement"}</th>
                    <th className="py-1 px-3 rn3-eyebrow text-right">{lang === "es" ? "Extraído" : "Parsed"}</th>
                    <th className="py-1 px-3 rn3-eyebrow text-right">{lang === "es" ? "Variación" : "Variance"}</th>
                    <th className="py-1 px-3 rn3-eyebrow text-right">{lang === "es" ? "Categorizado" : "Categorized"}</th>
                    <th className="py-1 px-3 rn3-eyebrow text-right">%</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ["deposits", lang === "es" ? "Depósitos" : "Deposits"],
                    ["atm_debit", "ATM / Debit"],
                    ["other_subtractions", lang === "es" ? "Otras salidas" : "Other −"],
                    ["service_fees", lang === "es" ? "Cargos bancarios" : "Service fees"],
                  ].map(([key, label]) => {
                    const s = selected.reconciliation.sections[key];
                    if (!s) return null;
                    const pct = s.parsed_total > 0 ? Math.round((s.categorized_total / s.parsed_total) * 100) : 0;
                    return (
                      <tr key={key} className="border-b border-[#F5F5F5]" data-testid={`stmt-recon-section-${key}`}>
                        <td className="py-1 pr-3">{label}</td>
                        <td className="py-1 px-3 text-right tabular-nums">{s.control_total != null ? fmt(s.control_total) : "—"}</td>
                        <td className="py-1 px-3 text-right tabular-nums">{fmt(s.parsed_total)}</td>
                        <td className={`py-1 px-3 text-right tabular-nums ${s.parse_ok ? "text-[#15803D]" : "text-[#8B1C22]"}`}>
                          {s.parse_variance == null ? "—" : (s.parse_variance === 0 ? "$0.00" : (s.parse_variance > 0 ? "+" : "") + fmt(s.parse_variance).replace("$-", "-$"))}
                        </td>
                        <td className={`py-1 px-3 text-right tabular-nums ${s.categorization_complete ? "text-[#15803D]" : "text-[#78350F]"}`}>
                          {fmt(s.categorized_total)}
                        </td>
                        <td className={`py-1 px-3 text-right tabular-nums ${pct === 100 ? "text-[#15803D]" : "text-[#78350F]"}`}>
                          {s.txn_count === 0 ? "—" : `${pct}%`}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {!selected.reconciliation.overall.control_reconciles && (
                <div className="mt-2 text-[10px] text-[#8B1C22]">
                  {lang === "es"
                    ? `Los totales del extracto no cuadran: ${fmt(selected.control_totals?.beginning_balance)} + entradas − salidas = ${fmt(selected.control_totals?.computed_ending_balance)}, pero el saldo final es ${fmt(selected.control_totals?.ending_balance)}.`
                    : `Statement totals do not reconcile: beg ${fmt(selected.control_totals?.beginning_balance)} + inflows − outflows = ${fmt(selected.control_totals?.computed_ending_balance)}, but ending balance is ${fmt(selected.control_totals?.ending_balance)}.`}
                </div>
              )}
            </div>
          )}

          {(selected.warnings || []).length > 0 && (
            <div className="px-4 py-2 bg-[#FEF3C7] border-t border-[#C4A252] text-xs text-[#78350F]">
              {selected.warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
            </div>
          )}

          <div className="overflow-x-auto">
            <div className="flex flex-col 2xl:flex-row gap-4 px-4 pt-3">
              {/* Flow filter sidebar — stacks above the table below 2xl so the
                  wide 9-column ledger always has full container width. */}
              <aside className="2xl:w-[200px] 2xl:flex-shrink-0" data-testid="stmt-flow-sidebar">
                <div className="border border-[#E5E5E5] bg-[#FDFBF7]">
                  <div className="px-3 py-2 border-b border-[#E5E5E5] flex items-center justify-between">
                    <div className="rn3-eyebrow text-[#404040]">{lang === "es" ? "Flujo" : "Flow"}</div>
                    {flowFilter !== null && (
                      <button
                        onClick={clearFlowFilter}
                        className="text-[10px] uppercase tracking-widest text-[#8B1C22] hover:underline"
                        data-testid="stmt-flow-clear"
                      >
                        {lang === "es" ? "Todo" : "All"}
                      </button>
                    )}
                  </div>
                  <div className="p-2 space-y-1">
                    {flowGroups.length === 0 ? (
                      <div className="text-[10px] text-[#737373] px-2 py-3 text-center">
                        {lang === "es" ? "Sin transacciones" : "No transactions"}
                      </div>
                    ) : flowGroups.map((g) => {
                      const active = isFlowActive(g.key);
                      const label = lang === "es" ? g.labelEs : g.labelEn;
                      return (
                        <button
                          key={g.key}
                          type="button"
                          onClick={() => toggleFlow(g.key)}
                          className={`w-full text-left px-2 py-1.5 border transition-colors flex items-start gap-2 ${
                            active
                              ? "border-[#C4A252] bg-white"
                              : "border-transparent bg-[#F5F5F5]/60 opacity-60 hover:opacity-90"
                          }`}
                          data-testid={`stmt-flow-toggle-${g.key}`}
                          aria-pressed={active}
                        >
                          <span className="mt-1 w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: g.dot }} />
                          <span className="flex-1 min-w-0">
                            <span className={`block text-xs font-medium ${active ? g.tone : "text-[#737373]"}`}>{label}</span>
                            <span className="block text-[10px] text-[#737373] tabular-nums">
                              {g.txns.length} · {fmt(g.total)}
                            </span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </aside>

              {/* Grouped transactions table */}
              <div className="flex-1 min-w-0 overflow-x-auto">
                {/* Shared typeahead sources — one <datalist> per column reused
                    by every row so accountants get autocomplete from previously
                    typed values in the same statement. */}
                <datalist id={`sub-list-${selected.id}`}>
                  {knownSubaccounts.map((v) => <option key={v} value={v} />)}
                </datalist>
                <datalist id={`notes-list-${selected.id}`}>
                  {knownNotes.map((v) => <option key={v} value={v} />)}
                </datalist>
                <table className="w-full text-xs">
                  <thead className="bg-[#FDFBF7]">
                    <tr className="text-left border-y border-[#E5E5E5]">
                      <th className="py-2 px-2 rn3-eyebrow">Date</th>
                      <th className="py-2 px-2 rn3-eyebrow">Description</th>
                      <th className="py-2 px-2 rn3-eyebrow text-right">{lang === "es" ? "Ingreso" : "Money In"}</th>
                      <th className="py-2 px-2 rn3-eyebrow text-right">{lang === "es" ? "Salida" : "Money Out"}</th>
                      <th className="py-2 px-2 rn3-eyebrow">{lang === "es" ? "Plan de cuentas" : "Chart of Accounts"}</th>
                      <th className="py-2 px-2 rn3-eyebrow">{lang === "es" ? "Subcuenta" : "Subaccount"}</th>
                      <th className="py-2 px-2 rn3-eyebrow">{lang === "es" ? "Notas" : "Notes"}</th>
                      <th className="py-2 px-2 rn3-eyebrow">{lang === "es" ? "Estado" : "Status"}</th>
                      <th className="py-2 px-2 rn3-eyebrow text-right">{lang === "es" ? "Acciones" : "Actions"}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleFlowGroups.length === 0 && (
                      <tr>
                        <td colSpan={9} className="py-6 text-center text-[11px] text-[#737373]" data-testid="stmt-flow-empty">
                          {lang === "es"
                            ? "Sin transacciones para el filtro seleccionado"
                            : "No transactions for the selected filter"}
                        </td>
                      </tr>
                    )}
                    {visibleFlowGroups.map((g) => (
                      <Fragment key={g.key}>
                        <tr className="bg-[#FAFAF7] border-y border-[#E5E5E5]" data-testid={`stmt-flow-group-${g.key}`}>
                          <td colSpan={9} className="py-1.5 px-3">
                            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest">
                              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: g.dot }} />
                              <span className={g.tone}>{lang === "es" ? g.labelEs : g.labelEn}</span>
                              <span className="text-[#737373] normal-case tracking-normal">
                                · {g.txns.length} {lang === "es" ? "txns" : "txns"} · {fmt(g.total)}
                              </span>
                            </div>
                          </td>
                        </tr>
                        {g.txns.map((t) => {
                          const src = t.category_source;
                          const badge = SOURCE_BADGE[src] || null;
                          const needsReview = !t.category_account_code || t.status === "needs_review";
                          const isDupe = !!t.duplicate_of;
                          const forcedDupe = isDupe && t.dupe_override;
                          const isFlashing = highlightTxnId === t.id;
                          return (
                            <tr
                              key={t.id}
                              className={`border-b border-[#F5F5F5] transition-colors ${isFlashing ? "!bg-[#DBEAFE] ring-2 ring-[#1E40AF]/40" : forcedDupe ? "bg-[#FEF3C7]" : isDupe ? "bg-[#FEE2E2]" : needsReview ? "bg-[#FEF7ED]" : ""}`}
                              data-testid={`stmt-txn-${t.id}`}
                            >
                              <td className="py-1.5 px-2 whitespace-nowrap tabular-nums">{t.date}</td>
                              <td
                                className={`py-1.5 px-2 max-w-[220px] ${selected.pdf_available ? "cursor-pointer group" : ""}`}
                                onClick={() => selected.pdf_available && highlightInPdf(t)}
                                title={(t.description || "") + (selected.pdf_available ? (lang === "es" ? " · Click para resaltar en el PDF" : " · Click to highlight in PDF") : "")}
                              >
                                <span className={`block truncate ${selected.pdf_available ? "group-hover:underline decoration-dotted decoration-[#C4A252]" : ""}`}>
                                  {t.description}
                                </span>
                                {isDupe && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); toggleDupeOverride(t); }}
                                    onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); toggleDupeOverride(t); }}
                                    className={`ml-2 text-[9px] uppercase tracking-widest border px-1.5 py-0.5 cursor-pointer hover:opacity-80 ${
                                      forcedDupe
                                        ? "border-[#166534] bg-[#DCFCE7] text-[#166534]"
                                        : "border-[#8B1C22] bg-[#FEE2E2] text-[#8B1C22]"
                                    }`}
                                    title={forcedDupe
                                      ? (lang === "es"
                                        ? "Se publicará a pesar del duplicado. Click para desactivar."
                                        : "Will post despite duplicate. Click to disarm.")
                                      : (lang === "es"
                                        ? "Ya existe en el registro. Click (o click derecho) para forzar publicación."
                                        : "Already exists in the register. Click (or right-click) to force post.")}
                                    data-testid={`stmt-txn-dupe-${t.id}`}
                                  >
                                    {forcedDupe
                                      ? (lang === "es" ? "DUPLICADO ✓" : "DUPE ✓")
                                      : (lang === "es" ? "DUPLICADO" : "DUPE")}
                                  </button>
                                )}
                              </td>
                              <td className={`py-1.5 px-2 text-right font-medium whitespace-nowrap tabular-nums ${t.direction === "inflow" ? "text-[#15803D]" : "text-[#737373]"}`} data-testid={`stmt-txn-in-${t.id}`}>
                                {t.direction === "inflow" ? fmt(t.amount) : "—"}
                              </td>
                              <td className={`py-1.5 px-2 text-right font-medium whitespace-nowrap tabular-nums ${t.direction === "outflow" ? "text-[#8B1C22]" : "text-[#737373]"}`} data-testid={`stmt-txn-out-${t.id}`}>
                                {t.direction === "outflow" ? fmt(t.amount) : "—"}
                              </td>
                              <td className="py-1.5 px-2">
                                <AccountCombobox
                                  value={t.category_account_code || ""}
                                  accounts={categoryAccounts}
                                  onPick={(code) => patchTxn(t.id, code)}
                                  onCreateNew={(prefill) => openNewAccountModal({ from: "txn", txnId: t.id }, prefill)}
                                  placeholder={lang === "es" ? "— PREGUNTAR —" : "— ASK ACCOUNTANT —"}
                                  testid={`stmt-txn-cat-${t.id}`}
                                />
                                {t.category_account_code && (() => {
                                  const acct = categoryAccounts.find((a) => a.code === t.category_account_code);
                                  if (!acct) return null;
                                  const detail = acct.detail_type || acct.subtype;
                                  return (
                                    <div className="text-[9px] text-[#737373] mt-0.5 uppercase tracking-widest">
                                      {acct.type}{detail ? ` · ${detail}` : ""}
                                    </div>
                                  );
                                })()}
                              </td>
                              <td className="py-1.5 px-2">
                                <input
                                  type="text"
                                  list={`sub-list-${selected.id}`}
                                  defaultValue={t.subaccount || ""}
                                  onBlur={(e) => {
                                    const v = e.target.value.trim();
                                    if ((t.subaccount || "") !== v) patchTxnField(t.id, { subaccount: v });
                                  }}
                                  placeholder="—"
                                  className="border border-[#E5E5E5] text-xs py-0.5 px-1 w-36 bg-white"
                                  data-testid={`stmt-txn-sub-${t.id}`}
                                />
                              </td>
                              <td className="py-1.5 px-2">
                                <input
                                  type="text"
                                  list={`notes-list-${selected.id}`}
                                  defaultValue={t.notes || ""}
                                  onBlur={(e) => {
                                    const v = e.target.value.trim();
                                    if ((t.notes || "") !== v) patchTxnField(t.id, { notes: v });
                                  }}
                                  placeholder={lang === "es" ? "notas…" : "notes…"}
                                  className="border border-[#E5E5E5] text-xs py-0.5 px-1 w-44 bg-white"
                                  data-testid={`stmt-txn-notes-${t.id}`}
                                />
                              </td>
                              <td className="py-1.5 px-2 whitespace-nowrap">
                                {t.user_approved ? (
                                  <span className="text-[9px] uppercase tracking-widest border border-[#166534] bg-[#DCFCE7] text-[#166534] px-1.5 py-0.5" data-testid={`stmt-txn-approved-${t.id}`}>
                                    ✓ {lang === "es" ? "APROBADO" : "APPROVED"}
                                  </span>
                                ) : badge ? (
                                  <span
                                    className={`text-[9px] uppercase tracking-widest border px-1.5 py-0.5 ${badge.cls}`}
                                    title={t.ai_suggestion?.reason ? `${t.ai_suggestion.reason} (conf ${(t.category_confidence * 100).toFixed(0)}%)` : ""}
                                  >
                                    {badge.label}
                                    {t.category_source === "ai_pending" && t.category_confidence != null && (
                                      <span className="ml-1 opacity-80">{Math.round(t.category_confidence * 100)}%</span>
                                    )}
                                  </span>
                                ) : (
                                  <span className="text-[9px] uppercase tracking-widest border border-[#8B1C22] text-[#8B1C22] px-1.5 py-0.5" data-testid={`stmt-txn-ask-${t.id}`}>
                                    {lang === "es" ? "PREGUNTAR" : "ASK"}
                                  </span>
                                )}
                              </td>
                              <td className="py-1.5 px-2 text-right whitespace-nowrap">
                                <div className="inline-flex items-center gap-1">
                                  {t.category_account_code && !t.user_approved && (
                                    <button
                                      onClick={() => patchTxnField(t.id, { user_approved: true })}
                                      className="text-[10px] uppercase tracking-wider bg-[#166534] hover:bg-[#14532D] text-white px-1.5 py-0.5 inline-flex items-center gap-0.5"
                                      title={lang === "es" ? "Aprobar transacción" : "Approve this transaction"}
                                      data-testid={`stmt-txn-approve-${t.id}`}
                                    >
                                      <Check className="w-3 h-3" /> {lang === "es" ? "Aprobar" : "Approve"}
                                    </button>
                                  )}
                                  {t.user_approved && (
                                    <button
                                      onClick={() => patchTxnField(t.id, { user_approved: false })}
                                      className="text-[9px] uppercase tracking-wider text-[#737373] hover:text-[#8B1C22] px-1"
                                      title={lang === "es" ? "Deshacer aprobación" : "Undo approval"}
                                      data-testid={`stmt-txn-unapprove-${t.id}`}
                                    >
                                      <Undo2 className="w-3 h-3" />
                                    </button>
                                  )}
                                  {t.category_account_code && (
                                    <button
                                      onClick={() => saveAsRule(t)}
                                      className="text-[#737373] hover:text-[#C4A252] inline-flex items-center gap-1"
                                      title={lang === "es" ? "Guardar como regla" : "Save as rule"}
                                      data-testid={`stmt-txn-save-rule-${t.id}`}
                                    >
                                      <Save className="w-3 h-3" />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
            </div>
            {previewOn && (
              <div className="bg-[#FAFAF7] lg:sticky lg:top-0 lg:self-start" data-testid="stmt-preview-pane">
                <div className="px-3 py-2 border-b border-[#E5E5E5] bg-white flex items-center justify-between">
                  <div className="rn3-eyebrow text-[#404040]">{lang === "es" ? "Documento fuente" : "Source document"}</div>
                  <button onClick={() => setPreviewOn(false)} className="text-[#737373] hover:text-[#8B1C22] text-xs">
                    ✕
                  </button>
                </div>
                {previewUrl ? (
                  <iframe
                    src={pdfSearchToken ? `${previewUrl}#search=${encodeURIComponent(pdfSearchToken)}` : previewUrl}
                    title="Statement PDF"
                    className="w-full h-[75vh] lg:h-[80vh] bg-white"
                    data-testid="stmt-preview-iframe"
                  />
                ) : (
                  <div className="p-10 text-center text-xs text-[#737373]">
                    {lang === "es" ? "Cargando PDF…" : "Loading PDF…"}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Post to Register — cash account picker modal */}

      {/* New Account modal — spawned from AccountCombobox "+ Add new account…" */}
      {newAccountModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4" data-testid="new-account-modal">
          <div className="bg-white max-w-md w-full border border-[#E5E5E5] shadow-2xl">
            <div className="px-5 py-3 border-b border-[#E5E5E5] bg-[#FDFBF7] flex items-center justify-between">
              <div>
                <div className="rn3-eyebrow text-[#8B1C22]">{lang === "es" ? "Plan de cuentas" : "Chart of Accounts"}</div>
                <div className="font-serif-display text-base">{lang === "es" ? "Nueva cuenta" : "New account"}</div>
              </div>
              <button onClick={() => setNewAccountModal(null)} className="text-[#737373] text-sm">✕</button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="rn3-eyebrow block mb-1">{lang === "es" ? "Tipo" : "Type"}</label>
                <select
                  value={newAcct.type}
                  onChange={(e) => {
                    const t = e.target.value;
                    // Suggest next code in that type when type changes
                    const codes = (accounts || [])
                      .filter((a) => a.type === t)
                      .map((a) => parseInt(a.code, 10))
                      .filter((n) => !isNaN(n))
                      .sort((a, b) => a - b);
                    const suggest = codes.length ? String(codes[codes.length - 1] + 10) : ({
                      asset: "1000", liability: "2000", equity: "3000",
                      income: "4000", cogs: "5000", expense: "6000",
                    }[t] || "6000");
                    setNewAcct({ ...newAcct, type: t, code: suggest });
                  }}
                  className="w-full border border-[#E5E5E5] text-sm py-2 px-2 bg-white"
                  data-testid="new-acct-type"
                >
                  <option value="asset">Asset · 1xxx</option>
                  <option value="liability">Liability · 2xxx</option>
                  <option value="equity">Equity · 3xxx</option>
                  <option value="income">Income · 4xxx</option>
                  <option value="cogs">COGS · 5xxx</option>
                  <option value="expense">Expense · 6xxx</option>
                </select>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-1">
                  <label className="rn3-eyebrow block mb-1">{lang === "es" ? "Código" : "Code"}</label>
                  <input
                    type="text"
                    value={newAcct.code}
                    onChange={(e) => setNewAcct({ ...newAcct, code: e.target.value.replace(/\D/g, "") })}
                    placeholder="6120"
                    className="w-full border border-[#E5E5E5] text-sm py-2 px-2 bg-white tabular-nums"
                    data-testid="new-acct-code"
                    maxLength={6}
                  />
                </div>
                <div className="col-span-2">
                  <label className="rn3-eyebrow block mb-1">{lang === "es" ? "Nombre" : "Name"}</label>
                  <input
                    type="text"
                    value={newAcct.name}
                    onChange={(e) => setNewAcct({ ...newAcct, name: e.target.value })}
                    placeholder={lang === "es" ? "p. ej. Combustible" : "e.g. Fuel & vehicle"}
                    className="w-full border border-[#E5E5E5] text-sm py-2 px-2 bg-white"
                    data-testid="new-acct-name"
                    autoFocus
                  />
                </div>
              </div>
              <div className="text-[10px] text-[#737373] leading-relaxed">
                {lang === "es"
                  ? "El código debe ser único en este libro. Convención: 1xxx activo · 2xxx pasivo · 3xxx patrimonio · 4xxx ingreso · 5xxx COGS · 6xxx gasto."
                  : "The code must be unique inside this book. Convention: 1xxx asset · 2xxx liability · 3xxx equity · 4xxx income · 5xxx COGS · 6xxx expense."}
              </div>
            </div>
            <div className="px-5 py-3 border-t border-[#E5E5E5] flex items-center justify-end gap-2">
              <button
                onClick={() => setNewAccountModal(null)}
                className="text-xs px-3 py-1.5 border border-[#E5E5E5] hover:bg-[#FDFBF7]"
                data-testid="new-acct-cancel"
              >
                {lang === "es" ? "Cancelar" : "Cancel"}
              </button>
              <button
                onClick={saveNewAccount}
                disabled={savingAcct}
                className="rn3-btn-primary text-xs inline-flex items-center gap-1 disabled:opacity-50"
                data-testid="new-acct-save"
              >
                <Save className="w-3 h-3" />
                {savingAcct ? "…" : (lang === "es" ? "Guardar y usar" : "Save & use")}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPost && selected && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4" data-testid="stmt-post-modal">
          <div className="bg-white max-w-md w-full border border-[#E5E5E5] shadow-2xl">
            <div className="px-5 py-3 border-b border-[#E5E5E5] bg-[#FDFBF7] flex items-center justify-between">
              <div>
                <div className="rn3-eyebrow text-[#8B1C22]">{lang === "es" ? "Publicar al registro" : "Post to register"}</div>
                <div className="font-serif-display text-base">{selected.filename}</div>
              </div>
              <button onClick={() => setShowPost(false)} className="text-[#737373] text-sm">✕</button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-xs text-[#404040] leading-relaxed">
                {lang === "es"
                  ? `Se crearán hasta ${selected.transactions?.length || 0} transacciones en el libro de ${book.name}, categorizadas exactamente como aparecen abajo. Elige la cuenta de efectivo (cheques, ahorros o tarjeta de crédito) contra la que se registrarán.`
                  : `Up to ${selected.transactions?.length || 0} transactions will be created in ${book.name}'s register, categorized exactly as shown below. Pick the cash account (checking, savings, or credit card) they hit.`}
              </p>
              {rollup.duplicates > 0 && (
                <div className="text-[11px] border border-[#8B1C22] bg-[#FEE2E2] text-[#8B1C22] px-3 py-2" data-testid="stmt-post-dupe-notice">
                  {lang === "es"
                    ? `⚠ ${rollup.duplicates} transacciones ya existen en el registro y se omitirán automáticamente.`
                    : `⚠ ${rollup.duplicates} transactions already exist in the register and will be skipped automatically.`}
                </div>
              )}
              {rollup.dupes_forced > 0 && (
                <div className="text-[11px] border border-[#166534] bg-[#DCFCE7] text-[#166534] px-3 py-2" data-testid="stmt-post-forced-notice">
                  {lang === "es"
                    ? `↳ ${rollup.dupes_forced} duplicadas marcadas para publicar de todos modos.`
                    : `↳ ${rollup.dupes_forced} duplicates flagged to force-post anyway.`}
                </div>
              )}
              <div>
                <label className="rn3-eyebrow block mb-1">{lang === "es" ? "Cuenta de efectivo" : "Cash account"}</label>
                <select
                  value={postCash}
                  onChange={(e) => setPostCash(e.target.value)}
                  className="w-full border border-[#E5E5E5] text-sm py-2 px-2 bg-white"
                  data-testid="stmt-post-cash-select"
                >
                  <option value="">— {lang === "es" ? "elegir" : "select"} —</option>
                  {cashAccounts.map((a) => (
                    <option key={a.code} value={a.code}>
                      {a.code} · {a.name}
                      {a.nickname ? ` · "${a.nickname}"` : ""}
                      {a.detail_type ? ` · ${a.detail_type}` : (a.nickname ? "" : ` (${a.type})`)}
                    </option>
                  ))}
                </select>
                {cashAccounts.length === 0 && (
                  <div className="text-[10px] text-[#8B1C22] mt-1">
                    {lang === "es"
                      ? "Este libro no tiene cuentas de activo o pasivo activas. Añade una desde el Plan de Cuentas."
                      : "This book has no active asset or liability accounts. Add one from the Chart of Accounts."}
                  </div>
                )}
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                <button onClick={() => setShowPost(false)} className="text-xs uppercase tracking-widest text-[#737373] px-3 py-2">
                  {lang === "es" ? "Cancelar" : "Cancel"}
                </button>
                <button
                  onClick={postToRegister}
                  disabled={!postCash || busy}
                  className="rn3-btn-primary inline-flex items-center gap-1 text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="stmt-post-confirm-btn"
                >
                  <Send className="w-3 h-3" />
                  {busy ? "…" : (lang === "es" ? "Publicar" : "Post")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Post to Register — one cash-account picker for every reconciled statement */}
      {showBulkPost && batchProgress?.results && (() => {
        const eligible = batchProgress.results.filter((r) => r.ok && r.badge === "pass" && !r.posted_at);
        return (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4" data-testid="stmt-bulk-post-modal">
            <div className="bg-white max-w-lg w-full border border-[#E5E5E5] shadow-2xl">
              <div className="px-5 py-3 border-b border-[#E5E5E5] bg-[#FDFBF7] flex items-center justify-between">
                <div>
                  <div className="rn3-eyebrow text-[#8B1C22]">{lang === "es" ? "Publicar en lote" : "Bulk post to register"}</div>
                  <div className="font-serif-display text-base">{eligible.length} {lang === "es" ? "extractos listos" : "reconciled statements"}</div>
                </div>
                <button onClick={() => setShowBulkPost(false)} className="text-[#737373] text-sm">✕</button>
              </div>
              <div className="p-5 space-y-4">
                <p className="text-xs text-[#404040] leading-relaxed">
                  {lang === "es"
                    ? `Cada uno de los ${eligible.length} extractos totalmente conciliados se publicará contra la misma cuenta de efectivo. Los duplicados detectados se omitirán automáticamente (a menos que estén marcados para forzar).`
                    : `All ${eligible.length} fully-reconciled statements will post against the same cash account. Detected duplicates are skipped automatically (unless flagged to force-post).`}
                </p>
                <div className="max-h-32 overflow-y-auto border border-[#F5F5F5] px-2 py-1 text-[10px] text-[#404040]" data-testid="stmt-bulk-post-list">
                  {eligible.map((r) => (
                    <div key={r.upload_id} className="py-0.5 flex justify-between gap-2">
                      <span className="truncate">{r.filename}</span>
                      <span className="text-[#737373]">{r.transaction_count} txns</span>
                    </div>
                  ))}
                </div>
                <div>
                  <label className="rn3-eyebrow block mb-1">{lang === "es" ? "Cuenta de efectivo" : "Cash account"}</label>
                  <select
                    value={bulkPostCash}
                    onChange={(e) => setBulkPostCash(e.target.value)}
                    className="w-full border border-[#E5E5E5] text-sm py-2 px-2 bg-white"
                    data-testid="stmt-bulk-post-cash-select"
                  >
                    <option value="">— {lang === "es" ? "elegir" : "select"} —</option>
                    {cashAccounts.map((a) => (
                      <option key={a.code} value={a.code}>
                        {a.code} · {a.name}
                        {a.nickname ? ` · "${a.nickname}"` : ""}
                        {a.detail_type ? ` · ${a.detail_type}` : (a.nickname ? "" : ` (${a.type})`)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center justify-end gap-2 pt-2">
                  <button onClick={() => setShowBulkPost(false)} className="text-xs uppercase tracking-widest text-[#737373] px-3 py-2">
                    {lang === "es" ? "Cancelar" : "Cancel"}
                  </button>
                  <button
                    onClick={bulkPost}
                    disabled={!bulkPostCash || busy}
                    className="rn3-btn-primary inline-flex items-center gap-1 text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="stmt-bulk-post-confirm-btn"
                  >
                    <Send className="w-3 h-3" />
                    {busy ? "…" : (lang === "es" ? `Publicar ${eligible.length}` : `Post ${eligible.length}`)}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default StatementsTab;

// ================================================================
// Review Queue Panel (Phase D)
// ================================================================
const ReviewQueuePanel = ({ book, accounts = [], lang, onApproved }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(true);
  const [drafts, setDrafts] = useState({}); // { `${sid}:${tid}`: {code, save_as_rule} }
  const [threshold, setThreshold] = useState(80); // percent, 0-100
  const [bulkSaveRule, setBulkSaveRule] = useState(true);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [q, setQ] = useState("");
  const [amtMin, setAmtMin] = useState("");
  const [amtMax, setAmtMax] = useState("");

  const categoryAccounts = useMemo(
    () => (accounts || [])
      .filter((a) => a.is_active !== false && !a.is_header)
      .sort((a, b) => a.code.localeCompare(b.code)),
    [accounts],
  );

  const filteredItems = useMemo(() => {
    const qq = q.trim().toLowerCase();
    const min = amtMin ? parseFloat(amtMin) : null;
    const max = amtMax ? parseFloat(amtMax) : null;
    return items.filter((r) => {
      if (qq) {
        const hay = `${r.description || ""} ${r.filename || ""} ${r.date || ""} ${r.ai_suggestion?.account_code || ""} ${r.ai_suggestion?.reason || ""}`.toLowerCase();
        if (!hay.includes(qq)) return false;
      }
      const amt = Math.abs(Number(r.amount || 0));
      if (min !== null && amt < min) return false;
      if (max !== null && amt > max) return false;
      return true;
    });
  }, [items, q, amtMin, amtMax]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/back-office/books/${book.id}/review-queue`);
      setItems(data.items || []);
      // seed drafts with each item's AI suggestion (or existing category)
      const next = {};
      (data.items || []).forEach((r) => {
        const key = `${r.statement_id}:${r.txn_id}`;
        next[key] = {
          code: r.category_account_code || r.ai_suggestion?.account_code || "",
          save_as_rule: true, // default per user's earlier choice
        };
      });
      setDrafts(next);
    } catch {
      // silent — panel just shows empty
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [book.id]);

  const setDraft = (key, patch) => setDrafts((d) => ({ ...d, [key]: { ...d[key], ...patch } }));

  const approve = async (r) => {
    const key = `${r.statement_id}:${r.txn_id}`;
    const draft = drafts[key] || {};
    if (!draft.code) {
      toast.error(lang === "es" ? "Selecciona una categoría" : "Pick a category first");
      return;
    }
    try {
      const { data } = await api.post(`/back-office/books/${book.id}/review-queue/approve`, {
        statement_id: r.statement_id,
        txn_id: r.txn_id,
        category_account_code: draft.code,
        save_as_rule: !!draft.save_as_rule,
      });
      toast.success(
        data.rule_created
          ? (lang === "es" ? `Aprobado · regla guardada "${data.rule_created.pattern}"` : `Approved · rule saved "${data.rule_created.pattern}"`)
          : (lang === "es" ? "Aprobado" : "Approved"),
      );
      load();
      if (onApproved) onApproved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Approve failed");
    }
  };

  // How many items would the current threshold clear? Preview for the button label.
  const eligibleCount = useMemo(() => {
    const th = threshold / 100;
    return items.filter((r) => (r.category_confidence || 0) >= th && r.ai_suggestion?.account_code).length;
  }, [items, threshold]);

  const bulkApprove = async () => {
    if (eligibleCount === 0) {
      toast.error(lang === "es" ? "Ninguna sugerencia cumple con el umbral" : "No suggestions meet the threshold");
      return;
    }
    if (!confirm(lang === "es"
      ? `¿Aprobar ${eligibleCount} sugerencias con confianza ≥ ${threshold}%?`
      : `Approve ${eligibleCount} AI suggestions with confidence ≥ ${threshold}%?`)) return;
    setBulkBusy(true);
    const tid = toast.loading(lang === "es" ? "Aprobando en lote…" : "Bulk approving…");
    try {
      const { data } = await api.post(`/back-office/books/${book.id}/review-queue/bulk-approve`, {
        confidence_threshold: threshold / 100,
        save_as_rule: bulkSaveRule,
      });
      toast.success(
        lang === "es"
          ? `${data.approved_count} aprobadas · ${data.rules_created} reglas · ${data.skipped_count} saltadas`
          : `${data.approved_count} approved · ${data.rules_created} rules · ${data.skipped_count} skipped`,
        { id: tid },
      );
      load();
      if (onApproved) onApproved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk approve failed", { id: tid });
    } finally {
      setBulkBusy(false);
    }
  };

  const count = items.length;

  return (
    <div className="border border-[#E5E5E5] bg-white" data-testid="review-queue-panel">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 bg-[#FDFBF7] border-b border-[#E5E5E5]"
        data-testid="review-queue-toggle"
      >
        <div className="flex items-center gap-2">
          <Inbox className="w-4 h-4 text-[#8B1C22]" />
          <span className="rn3-eyebrow text-[#8B1C22]">{lang === "es" ? "Cola de revisión" : "Ask accountant queue"}</span>
          <span className={`text-[10px] px-1.5 py-0.5 border ${count > 0 ? "border-[#8B1C22] text-[#8B1C22]" : "border-[#A3A3A3] text-[#737373]"}`}>
            {count}
          </span>
        </div>
        <span className="text-[#737373] text-xs">{open ? "▾" : "▸"}</span>
      </button>

      {open && (
        <div>
          {/* Bulk approve toolbar (shown when there are eligible AI picks) */}
          {count > 0 && (
            <div className="flex flex-wrap items-center gap-3 px-4 py-2 bg-[#FAFAF7] border-b border-[#F5F5F5] text-[11px]" data-testid="bulk-approve-toolbar">
              <span className="rn3-eyebrow text-[#404040]">{lang === "es" ? "Aprobación en lote" : "Bulk approve"}</span>
              <label className="inline-flex items-center gap-1">
                {lang === "es" ? "Confianza ≥" : "Confidence ≥"}
                <input
                  type="number"
                  min={50}
                  max={100}
                  step={5}
                  value={threshold}
                  onChange={(e) => setThreshold(Math.max(50, Math.min(100, Number(e.target.value) || 80)))}
                  className="w-14 border border-[#E5E5E5] px-1 py-0.5 tabular-nums text-right bg-white"
                  data-testid="bulk-threshold-input"
                />
                <span>%</span>
              </label>
              <label className="inline-flex items-center gap-1" data-testid="bulk-save-rule">
                <input
                  type="checkbox"
                  checked={bulkSaveRule}
                  onChange={(e) => setBulkSaveRule(e.target.checked)}
                  className="accent-[#8B1C22]"
                />
                {lang === "es" ? "guardar reglas" : "save rules"}
              </label>
              <button
                onClick={bulkApprove}
                disabled={bulkBusy || eligibleCount === 0}
                className="rn3-btn-primary inline-flex items-center gap-1 text-[10px] px-2 py-1 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="bulk-approve-btn"
              >
                <Check className="w-3 h-3" />
                {bulkBusy
                  ? "…"
                  : (lang === "es"
                    ? `Aprobar ${eligibleCount}`
                    : `Approve ${eligibleCount}`)}
              </button>
            </div>
          )}
          {loading ? (
            <div className="p-6 text-center text-sm text-[#737373]">{lang === "es" ? "Cargando…" : "Loading…"}</div>
          ) : count === 0 ? (
            <div className="p-6 text-center text-sm text-[#737373]" data-testid="review-queue-empty">
              {lang === "es"
                ? "Todo revisado. Nada pendiente."
                : "Inbox zero. Nothing waiting for review."}
            </div>
          ) : (
            <>
              <div className="px-3 py-2 border-b border-[#F5F5F5] bg-white flex flex-wrap gap-2 items-center">
                <input
                  type="text"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={lang === "es" ? "Buscar descripción, archivo, fecha, código…" : "Search description, file, date, code…"}
                  className="border border-[#E5E5E5] text-xs py-1 px-2 bg-white flex-1 min-w-[220px]"
                  data-testid="review-queue-search"
                />
                <input
                  type="number"
                  step="0.01"
                  value={amtMin}
                  onChange={(e) => setAmtMin(e.target.value)}
                  placeholder={lang === "es" ? "min $" : "min $"}
                  className="w-20 border border-[#E5E5E5] text-xs py-1 px-2 bg-white tabular-nums"
                  data-testid="review-queue-amtmin"
                />
                <input
                  type="number"
                  step="0.01"
                  value={amtMax}
                  onChange={(e) => setAmtMax(e.target.value)}
                  placeholder={lang === "es" ? "max $" : "max $"}
                  className="w-20 border border-[#E5E5E5] text-xs py-1 px-2 bg-white tabular-nums"
                  data-testid="review-queue-amtmax"
                />
                {(q || amtMin || amtMax) && (
                  <button
                    onClick={() => { setQ(""); setAmtMin(""); setAmtMax(""); }}
                    className="text-[10px] uppercase tracking-widest text-[#8B1C22] hover:underline px-2"
                    data-testid="review-queue-clear"
                  >
                    {lang === "es" ? "Limpiar" : "Clear"}
                  </button>
                )}
                <span className="text-[10px] text-[#737373] tabular-nums ml-auto">
                  {filteredItems.length} / {items.length}
                </span>
              </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-[#FAFAF7]">
                  <tr className="text-left border-b border-[#E5E5E5]">
                    <th className="py-2 px-3 rn3-eyebrow">Date</th>
                    <th className="py-2 px-3 rn3-eyebrow">{lang === "es" ? "Descripción" : "Description"}</th>
                    <th className="py-2 px-3 rn3-eyebrow text-right">Amount</th>
                    <th className="py-2 px-3 rn3-eyebrow">{lang === "es" ? "Sugerencia IA" : "AI suggestion"}</th>
                    <th className="py-2 px-3 rn3-eyebrow">{lang === "es" ? "Asignar a" : "Assign to"}</th>
                    <th className="py-2 px-3 rn3-eyebrow">{lang === "es" ? "Regla" : "Rule"}</th>
                    <th className="py-2 px-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.map((r) => {
                    const key = `${r.statement_id}:${r.txn_id}`;
                    const draft = drafts[key] || { code: "", save_as_rule: true };
                    const aiCode = r.ai_suggestion?.account_code;
                    const aiConf = r.category_confidence;
                    const aiReason = r.ai_suggestion?.reason;
                    const aiAccount = categoryAccounts.find((a) => a.code === aiCode);
                    return (
                      <tr key={key} className="border-b border-[#F5F5F5] hover:bg-[#FDFBF7]/50" data-testid={`review-row-${key}`}>
                        <td className="py-1.5 px-3 whitespace-nowrap">{r.date}</td>
                        <td className="py-1.5 px-3">
                          <div>{r.description}</div>
                          <div className="text-[10px] text-[#737373] mt-0.5">{r.filename}</div>
                        </td>
                        <td className={`py-1.5 px-3 text-right font-medium whitespace-nowrap ${r.direction === "inflow" ? "text-[#15803D]" : "text-[#8B1C22]"}`}>
                          {r.direction === "inflow" ? "+" : "−"}{fmt(r.amount)}
                        </td>
                        <td className="py-1.5 px-3 max-w-[220px]">
                          {aiCode ? (
                            <div>
                              <div className="text-[11px]">
                                <span className="font-medium">{aiCode}</span> {aiAccount ? `· ${aiAccount.name}` : ""}
                              </div>
                              <div className="text-[10px] text-[#737373] flex items-center gap-1">
                                <span className="text-[#5B21B6]">{aiConf != null ? `${Math.round(aiConf * 100)}%` : "?"}</span>
                                {aiReason && <span className="truncate italic" title={aiReason}>· {aiReason}</span>}
                              </div>
                            </div>
                          ) : (
                            <span className="text-[10px] text-[#737373] italic">{lang === "es" ? "sin sugerencia" : "no suggestion"}</span>
                          )}
                        </td>
                        <td className="py-1.5 px-3">
                          <AccountCombobox
                            value={draft.code}
                            accounts={categoryAccounts}
                            onPick={(code) => setDraft(key, { code })}
                            placeholder={lang === "es" ? "— elegir —" : "— select —"}
                            testid={`review-cat-${key}`}
                          />
                        </td>
                        <td className="py-1.5 px-3">
                          <label className="inline-flex items-center gap-1 text-[10px] text-[#404040]" data-testid={`review-save-rule-${key}`}>
                            <input
                              type="checkbox"
                              checked={!!draft.save_as_rule}
                              onChange={(e) => setDraft(key, { save_as_rule: e.target.checked })}
                              className="accent-[#8B1C22]"
                            />
                            {lang === "es" ? "guardar" : "save"}
                          </label>
                        </td>
                        <td className="py-1.5 px-3 text-right">
                          <button
                            onClick={() => approve(r)}
                            className="rn3-btn-primary inline-flex items-center gap-1 text-[10px] px-2 py-1"
                            data-testid={`review-approve-${key}`}
                          >
                            <Check className="w-3 h-3" />
                            {lang === "es" ? "Aprobar" : "Approve"}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};


// ================================================================
// Rules Registry Panel — book-scoped bo_rules CRUD in the statement view
// ================================================================
const MATCH_TYPES = [
  { value: "contains", label: "contains" },
  { value: "starts_with", label: "starts with" },
  { value: "regex", label: "regex" },
];

const RulesPanel = ({ book, accounts = [], lang }) => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ pattern: "", match_type: "contains", category_account_code: "", note: "" });
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState({ key: "created_at", dir: "desc" });

  const categoryAccounts = useMemo(
    () => (accounts || [])
      .filter((a) => a.is_active !== false && !a.is_header && ["income", "cogs", "expense", "liability"].includes(a.type))
      .sort((a, b) => a.code.localeCompare(b.code)),
    [accounts],
  );

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/back-office/books/${book.id}/rules`);
      setRules(data);
    } catch {
      // silent — panel hides its contents on failure
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [book.id]);

  const create = async () => {
    if (!draft.pattern.trim() || !draft.category_account_code) {
      toast.error(lang === "es" ? "Patrón y categoría son obligatorios" : "Pattern and category are required");
      return;
    }
    try {
      await api.post(`/back-office/books/${book.id}/rules`, {
        pattern: draft.pattern.trim(),
        match_type: draft.match_type,
        category_account_code: draft.category_account_code,
        case_sensitive: false,
        note: draft.note.trim() || null,
      });
      toast.success(lang === "es" ? "Regla creada" : "Rule created");
      setDraft({ pattern: "", match_type: "contains", category_account_code: "", note: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Create failed");
    }
  };

  const toggle = async (r) => {
    try {
      await api.patch(`/back-office/books/${book.id}/rules/${r.id}`, { is_active: !r.is_active });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Toggle failed");
    }
  };

  const remove = async (r) => {
    if (!confirm(lang === "es" ? `¿Eliminar la regla "${r.pattern}"?` : `Delete rule "${r.pattern}"?`)) return;
    try {
      await api.delete(`/back-office/books/${book.id}/rules/${r.id}`);
      toast.success(lang === "es" ? "Eliminada" : "Deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const editCategory = async (r, newCode) => {
    if (!newCode || newCode === r.category_account_code) return;
    try {
      await api.patch(`/back-office/books/${book.id}/rules/${r.id}`, { category_account_code: newCode });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    }
  };

  const activeCount = rules.filter((r) => r.is_active).length;
  const showToolbar = rules.length >= 8;

  const accountLabel = (code) => {
    const a = categoryAccounts.find((x) => x.code === code);
    return a ? `${a.code} ${a.name}` : code;
  };

  // Filter (by pattern / category code+name / note) + sort. Runs client-side —
  // even at 500 rules this stays instant.
  const displayed = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = rules;
    if (q) {
      list = list.filter((r) =>
        (r.pattern || "").toLowerCase().includes(q)
        || accountLabel(r.category_account_code).toLowerCase().includes(q)
        || (r.note || "").toLowerCase().includes(q),
      );
    }
    const dir = sort.dir === "asc" ? 1 : -1;
    const key = sort.key;
    return [...list].sort((a, b) => {
      let av = a[key];
      let bv = b[key];
      if (key === "category_account_code") { av = accountLabel(av); bv = accountLabel(bv); }
      if (key === "hit_count") { av = Number(av || 0); bv = Number(bv || 0); }
      if (typeof av === "string") av = av.toLowerCase();
      if (typeof bv === "string") bv = bv.toLowerCase();
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [rules, query, sort, categoryAccounts]);

  const toggleSort = (key) => {
    setSort((s) => s.key === key
      ? { key, dir: s.dir === "asc" ? "desc" : "asc" }
      : { key, dir: key === "hit_count" ? "desc" : "asc" });
  };
  const arrow = (key) => sort.key === key ? (sort.dir === "asc" ? " ↑" : " ↓") : "";

  return (
    <div className="border border-[#E5E5E5] bg-white" data-testid="rules-panel">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 bg-[#FDFBF7] border-b border-[#E5E5E5]"
        data-testid="rules-panel-toggle"
      >
        <div className="flex items-center gap-2">
          <Settings className="w-4 h-4 text-[#8B1C22]" />
          <span className="rn3-eyebrow text-[#8B1C22]">
            {lang === "es" ? "Reglas de categorización" : "Categorization rules"}
          </span>
          <span className={`text-[10px] px-1.5 py-0.5 border ${activeCount > 0 ? "border-[#166534] text-[#166534] bg-[#DCFCE7]" : "border-[#A3A3A3] text-[#737373]"}`}>
            {activeCount} active
          </span>
          {rules.length !== activeCount && (
            <span className="text-[10px] px-1.5 py-0.5 border border-[#A3A3A3] text-[#737373]">
              {rules.length - activeCount} paused
            </span>
          )}
        </div>
        <span className="text-[#737373] text-xs">{open ? "▾" : "▸"}</span>
      </button>

      {open && (
        <div className="p-4">
          {/* Add new rule row */}
          <div className="grid grid-cols-1 md:grid-cols-[1.4fr,120px,1fr,1fr,auto] gap-2 items-end mb-4" data-testid="rules-new-row">
            <div>
              <label className="rn3-eyebrow text-[10px] block mb-0.5">{lang === "es" ? "Patrón" : "Pattern"}</label>
              <input
                value={draft.pattern}
                onChange={(e) => setDraft((d) => ({ ...d, pattern: e.target.value }))}
                placeholder="e.g. CHEVRON"
                className="w-full border border-[#E5E5E5] text-xs px-2 py-1 bg-white"
                data-testid="rules-new-pattern"
              />
            </div>
            <div>
              <label className="rn3-eyebrow text-[10px] block mb-0.5">{lang === "es" ? "Modo" : "Match"}</label>
              <select
                value={draft.match_type}
                onChange={(e) => setDraft((d) => ({ ...d, match_type: e.target.value }))}
                className="w-full border border-[#E5E5E5] text-xs px-1 py-1 bg-white"
                data-testid="rules-new-match-type"
              >
                {MATCH_TYPES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
            <div>
              <label className="rn3-eyebrow text-[10px] block mb-0.5">{lang === "es" ? "Categoría" : "Category"}</label>
              <select
                value={draft.category_account_code}
                onChange={(e) => setDraft((d) => ({ ...d, category_account_code: e.target.value }))}
                className="w-full border border-[#E5E5E5] text-xs px-1 py-1 bg-white"
                data-testid="rules-new-category"
              >
                <option value="">— {lang === "es" ? "elegir" : "select"} —</option>
                {categoryAccounts.map((a) => (
                  <option key={a.code} value={a.code}>{a.code} · {a.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="rn3-eyebrow text-[10px] block mb-0.5">{lang === "es" ? "Nota (opcional)" : "Note (optional)"}</label>
              <input
                value={draft.note}
                onChange={(e) => setDraft((d) => ({ ...d, note: e.target.value }))}
                placeholder=""
                className="w-full border border-[#E5E5E5] text-xs px-2 py-1 bg-white"
                data-testid="rules-new-note"
              />
            </div>
            <button
              onClick={create}
              className="rn3-btn-primary inline-flex items-center gap-1 text-xs h-[26px] px-3"
              data-testid="rules-new-submit"
            >
              <Plus className="w-3 h-3" />
              {lang === "es" ? "Añadir" : "Add"}
            </button>
          </div>

          {/* Search toolbar (auto-shows once a book crosses 8 rules) */}
          {showToolbar && (
            <div className="flex flex-wrap items-center gap-2 mb-3 text-xs" data-testid="rules-toolbar">
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={lang === "es"
                  ? "Buscar por patrón, categoría o nota…"
                  : "Search pattern, category, or note…"}
                className="border border-[#E5E5E5] px-2 py-1 flex-1 min-w-[220px] bg-white"
                data-testid="rules-search"
              />
              <span className="text-[10px] text-[#737373]">
                {query
                  ? (lang === "es"
                    ? `${displayed.length} / ${rules.length} coincidencias`
                    : `${displayed.length} / ${rules.length} match`)
                  : (lang === "es"
                    ? `${rules.length} reglas`
                    : `${rules.length} rules`)}
              </span>
              {query && (
                <button
                  onClick={() => setQuery("")}
                  className="text-[10px] uppercase tracking-widest text-[#737373] hover:text-[#8B1C22] border border-[#E5E5E5] px-2 py-0.5"
                  data-testid="rules-search-clear"
                >
                  {lang === "es" ? "Limpiar" : "Clear"}
                </button>
              )}
            </div>
          )}

          {/* Existing rules table */}
          {loading ? (
            <div className="p-4 text-center text-xs text-[#737373]">{lang === "es" ? "Cargando…" : "Loading…"}</div>
          ) : rules.length === 0 ? (
            <div className="p-4 text-center text-xs text-[#737373]" data-testid="rules-empty">
              {lang === "es"
                ? "Sin reglas todavía. Añade la primera arriba para que las nuevas transacciones se categoricen automáticamente."
                : "No rules yet. Add your first above so new transactions get auto-categorized."}
            </div>
          ) : displayed.length === 0 ? (
            <div className="p-4 text-center text-xs text-[#737373]" data-testid="rules-search-empty">
              {lang === "es"
                ? `No hay reglas que coincidan con "${query}"`
                : `No rules match "${query}"`}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-[#FAFAF7]">
                  <tr className="text-left border-y border-[#E5E5E5]">
                    <th className="py-1.5 px-2 rn3-eyebrow cursor-pointer hover:text-[#8B1C22]" onClick={() => toggleSort("pattern")} data-testid="rules-sort-pattern">
                      {lang === "es" ? "Patrón" : "Pattern"}{arrow("pattern")}
                    </th>
                    <th className="py-1.5 px-2 rn3-eyebrow cursor-pointer hover:text-[#8B1C22]" onClick={() => toggleSort("match_type")} data-testid="rules-sort-match">
                      {lang === "es" ? "Modo" : "Match"}{arrow("match_type")}
                    </th>
                    <th className="py-1.5 px-2 rn3-eyebrow cursor-pointer hover:text-[#8B1C22]" onClick={() => toggleSort("category_account_code")} data-testid="rules-sort-cat">
                      {lang === "es" ? "Categoría" : "Category"}{arrow("category_account_code")}
                    </th>
                    <th className="py-1.5 px-2 rn3-eyebrow text-right cursor-pointer hover:text-[#8B1C22]" onClick={() => toggleSort("hit_count")} data-testid="rules-sort-hits">
                      Hits{arrow("hit_count")}
                    </th>
                    <th className="py-1.5 px-2 rn3-eyebrow cursor-pointer hover:text-[#8B1C22]" onClick={() => toggleSort("note")} data-testid="rules-sort-note">
                      {lang === "es" ? "Nota" : "Note"}{arrow("note")}
                    </th>
                    <th className="py-1.5 px-2 rn3-eyebrow text-right">{lang === "es" ? "Acciones" : "Actions"}</th>
                  </tr>
                </thead>
                <tbody>
                  {displayed.map((r) => (
                    <tr key={r.id} className={`border-b border-[#F5F5F5] ${r.is_active ? "" : "opacity-50"}`} data-testid={`rule-row-${r.id}`}>
                      <td className="py-1.5 px-2 font-medium">{r.pattern}</td>
                      <td className="py-1.5 px-2 text-[#737373]">{r.match_type}</td>
                      <td className="py-1.5 px-2">
                        <select
                          value={r.category_account_code}
                          onChange={(e) => editCategory(r, e.target.value)}
                          className="border border-[#E5E5E5] text-[11px] py-0.5 px-1 min-w-[160px] bg-white"
                          data-testid={`rule-cat-${r.id}`}
                        >
                          {categoryAccounts.map((a) => (
                            <option key={a.code} value={a.code}>{a.code} · {a.name}</option>
                          ))}
                        </select>
                      </td>
                      <td className="py-1.5 px-2 text-right tabular-nums">{r.hit_count || 0}</td>
                      <td className="py-1.5 px-2 text-[#737373] truncate max-w-[220px]">{r.note || ""}</td>
                      <td className="py-1.5 px-2 text-right whitespace-nowrap">
                        <button
                          onClick={() => toggle(r)}
                          className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest border px-1.5 py-0.5 mr-1 ${
                            r.is_active
                              ? "border-[#166534] text-[#166534] hover:bg-[#DCFCE7]"
                              : "border-[#737373] text-[#737373] hover:bg-[#F5F5F5]"
                          }`}
                          data-testid={`rule-toggle-${r.id}`}
                        >
                          <Power className="w-3 h-3" />
                          {r.is_active
                            ? (lang === "es" ? "Activa" : "On")
                            : (lang === "es" ? "Pausada" : "Off")}
                        </button>
                        <button
                          onClick={() => remove(r)}
                          className="text-[#8B1C22] hover:bg-[#FEE2E2] p-1"
                          title={lang === "es" ? "Eliminar" : "Delete"}
                          data-testid={`rule-delete-${r.id}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
